import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, Card, Table, Button, Upload, message, Statistic, Row, Col, Select, Modal, Input, Space, Spin, Tag, Typography, Divider, Image, Alert, Empty, DatePicker } from 'antd';
import type { UploadProps } from 'antd';
import { DownOutlined, UpOutlined, UploadOutlined, DeleteOutlined, SearchOutlined, EditOutlined, LockOutlined, SwapOutlined, StopOutlined, CheckCircleOutlined } from '@ant-design/icons';
import {
  Bell,
  BriefcaseBusiness,
  Building2,
  ChartNoAxesColumn,
  ChevronDown,
  ChevronUp,
  CircleCheck,
  ClipboardCheck,
  Clock3,
  Gauge,
  Mail,
  MapPin,
  MonitorDot,
  Users as UsersIcon,
  UsersRound,
} from 'lucide';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import CumulativeStatistics from '../components/CumulativeStatistics';
import AdminMailJournal from '../components/AdminMailJournal';
import {  } from '../config/features';
import MorphingIcon from '../components/MorphingIcon';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import api, { getStaticImageUrl } from '../services/api';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { getTimezoneByRegion } from '../utils/timezone';
import { formatTime } from '../utils/formatTime';
import { getApiErrorMessage } from '../utils/apiError';
import {  } from '../constants/testSubgroups';
import useUserStatisticsPeriod, { StatisticsPeriod } from '../hooks/useUserStatisticsPeriod';
import AttemptsExcelExportButton from '../components/AttemptsExcelExportButton';
import EmployeeRetakeStatistics from '../components/EmployeeRetakeStatistics';
import './AdminPanel.css';

// Подключаем плагины dayjs
dayjs.extend(utc);
dayjs.extend(timezone);

const { RangePicker } = DatePicker;
const { Text } = Typography;
const { Option } = Select;
const { TabPane } = Tabs;

const AUTH_ADMIN_USERS_URL = 'https://auth.pikinlab.ru/admin/users';
const USER_STRUCTURE_STORAGE_KEY = 'febest.admin.user-structure-expanded';
const ALL_USER_DEPARTMENTS = '__all__';

function getAuthAdminUserUrl(email?: string): string {
  const normalizedEmail = String(email || '').trim();
  if (!normalizedEmail) return AUTH_ADMIN_USERS_URL;
  return `${AUTH_ADMIN_USERS_URL}?search=${encodeURIComponent(normalizedEmail)}`;
}

function normalizeUserDirectoryValue(value: unknown): string {
  return String(value || '').trim().toLocaleLowerCase('ru-RU');
}

function getUserDepartment(user: any): string {
  return String(user.department || '').trim()
    || (user.role === 'admin' ? 'Администрация' : 'Без отдела');
}

/** Приводит введённое название блока к значению API: products | vehicles */







interface UserStatistic {
  user_id: number;
  user_name: string;
  department: string;
  position: string;
  work_experience: string;
  region: string;
  attempts_count: number;
  passed_count: number;
  failed_count: number;
  average_score: number;
}

interface AdminPanelProps {
  initialTab?: string;
}

interface AdminNewsItem {
  date: string;
  label: string;
  color: string;
  tags?: Array<{
    label: string;
    color: string;
  }>;
  title: string;
  changes: string[];
  instructions: string[];
}

const adminNewsItems: AdminNewsItem[] = [
  {
    date: '25.07.2026',
    label: 'Обновление',
    color: 'blue',
    tags: [
      { label: 'Пользователи', color: 'geekblue' },
      { label: 'Тесты', color: 'blue' },
      { label: 'Назначения', color: 'purple' },
      { label: 'Вопросы', color: 'cyan' },
    ],
    title: 'Из выбранных вопросов создаётся отдельный тест с названием',
    changes: [
      'Несколько отмеченных непройденных вопросов теперь собираются в один новый тест, а не объединяются в один вопрос.',
      'Перед созданием система открывает окно и предлагает администратору указать или изменить название теста.',
      'Каждый вопрос копируется отдельно вместе со своими вариантами ответов, изображениями и пояснениями.',
      'После создания новый тест автоматически назначается выбранному сотруднику.',
    ],
    instructions: [
      'Откройте вкладку "Пользователи" и раскройте строку нужного сотрудника.',
      'Отметьте непройденные вопросы и нажмите "Собрать тест".',
      'Введите название и нажмите "Создать и назначить".',
    ],
  },
  {
    date: '25.07.2026',
    label: 'Назначения',
    color: 'purple',
    tags: [
      { label: 'Сотрудники', color: 'geekblue' },
      { label: 'Назначения', color: 'purple' },
      { label: 'Вопросы', color: 'cyan' },
    ],
    title: 'Сотруднику можно назначить только непройденные вопросы',
    changes: [
      'В таблице пользователей у каждого сотрудника появился раскрываемый список вопросов, на которые он ещё не отправлял ответ.',
      'Одинаковые вопросы из разных тестов показываются один раз, а вопросы из активных назначений помечаются и не выбираются повторно.',
      'Выбранные вопросы собираются в один новый индивидуальный тест с заданным администратором названием и назначаются этому сотруднику.',
    ],
    instructions: [
      'Откройте вкладку "Пользователи".',
      'Нажмите стрелку слева от сотрудника и дождитесь загрузки списка непройденных вопросов.',
      'Отметьте нужные вопросы чекбоксами и нажмите "Собрать тест".',
      'Введите название нового теста и подтвердите создание и назначение.',
    ],
  },
  {
    date: '25.07.2026',
    label: 'Поиск',
    color: 'green',
    tags: [
      { label: 'Поиск', color: 'green' },
      { label: 'Тесты', color: 'blue' },
      { label: 'Вопросы', color: 'cyan' },
    ],
    title: 'Поиск тестов работает по текстам вопросов',
    changes: [
      'Строка поиска в управлении тестами теперь проверяет не только название, блок и категорию, но и тексты вопросов.',
      'Если искомая фраза встречается внутри вопроса, в таблице остаются все тесты, содержащие этот вопрос.',
      'Поиск выполняется по уже загруженным данным без отдельного запроса при каждом вводе.',
    ],
    instructions: [
      'Откройте вкладку "Тесты".',
      'Введите слово или фразу из нужного вопроса в строку поиска.',
      'Откройте найденный тест через "Управление вопросами" для проверки или редактирования.',
      'Очистите строку поиска крестиком, чтобы вернуть полный список.',
    ],
  },
  {
    date: '17.06.2026',
    label: 'Импорт',
    color: 'cyan',
    title: 'Импорт рабочих данных сотрудников из Excel',
    changes: [
      'Появилась загрузка Excel-файла во вкладке "Пользователи" для обновления отдела, должности, стажа работы и региона.',
      'Если в файле нет одной из рабочих колонок или отдельная ячейка пустая, импорт не считает это ошибкой и не затирает старое значение.',
      'Сотрудник ищется по ID, email, логину или ФИО; новые пользователи через этот импорт не создаются.',
    ],
    instructions: [
      'Откройте вкладку "Пользователи".',
      'Нажмите "Импорт данных" и выберите файл Excel.',
      'Проверьте выбранный файл и нажмите "Импортировать".',
      'После импорта проверьте предупреждения по строкам, если какие-то сотрудники не были найдены.',
    ],
  },
  {
    date: '17.06.2026',
    label: 'Сотрудники',
    color: 'geekblue',
    title: 'Рабочие данные сотрудников и статистика по стажу',
    changes: [
      'В карточке сотрудника теперь редактируются отдел, должность, стаж работы и регион.',
      'Стаж работы добавлен в статистику: отдельная сводка, рейтинг пользователей, таблица попыток и детали проблемных вопросов.',
      'Новые рабочие данные отображаются в админских таблицах без изменения уже пройденных тестов.',
    ],
    instructions: [
      'Откройте вкладку "Пользователи".',
      'Нажмите "Редактировать" в строке нужного сотрудника.',
      'Заполните отдел, должность, стаж работы и регион, затем сохраните.',
      'Откройте вкладку "Статистика", чтобы увидеть сводку по стажу работы и обновленные таблицы.',
    ],
  },
  {
    date: '02.06.2026',
    label: 'Доступ к тестам',
    color: 'green',
    title: 'Повторное прохождение теста только с разрешения админа',
    changes: [
      'Если сотрудник уже прошел тест, он видит его как пройденный и неактивный.',
      'Повторно открыть или сдать тот же тест без нового назначения нельзя.',
      'Новое назначение от админа считается разрешением на повторное прохождение.',
    ],
    instructions: [
      'Откройте вкладку "Тесты".',
      'Нажмите "Назначить" у нужного теста или используйте индивидуальные назначения.',
      'Выберите сотрудника и сохраните назначение. После этого у сотрудника снова появится доступ.',
    ],
  },
  {
    date: '02.06.2026',
    label: 'Результаты',
    color: 'blue',
    title: 'Результаты учитывают исправления правильных ответов',
    changes: [
      'Ответ сотрудника сохраняется как факт выбранного варианта.',
      'Если админ исправил правильный ответ в вопросе, результат можно пересчитать корректно.',
      'Ситуация "ответ был выбран, но система показывает не выбран" больше не должна искажать факт ответа.',
    ],
    instructions: [
      'Откройте тест и исправьте правильный ответ в вопросе.',
      'Проверьте результат сотрудника в статистике или деталях попытки.',
      'Если нужна ручная проверка конкретной попытки, откройте ее в разделе "Статистика".',
    ],
  },
  {
    date: '02.06.2026',
    label: 'Попытки',
    color: 'red',
    title: 'Выборочное удаление попыток',
    changes: [
      'Теперь можно удалить одну или несколько попыток, не очищая всю историю.',
      'В таблице попыток появились чекбоксы для выбора строк.',
      'Массовая кнопка "Удалить все попытки" осталась отдельно.',
    ],
    instructions: [
      'Откройте вкладку "Статистика".',
      'В блоке "Все попытки прохождения тестов" отметьте нужные строки чекбоксами.',
      'Нажмите "Удалить выбранные" и подтвердите действие.',
    ],
  },
  {
    date: '02.06.2026',
    label: 'История',
    color: 'purple',
    title: 'История назначений: сотрудник, месяц, тесты',
    changes: [
      'В индивидуальных назначениях появилась история в виде папок.',
      'Сначала идет сотрудник, внутри месяцы, внутри месяца назначенные тесты.',
      'По каждому назначению видно дату, статус, результат, дедлайн и кто назначил тест.',
    ],
    instructions: [
      'Откройте вкладку "Тесты".',
      'Найдите блок "История назначений по сотрудникам" под индивидуальными назначениями.',
      'Раскройте сотрудника, затем месяц, чтобы увидеть назначенные тесты.',
    ],
  },
  {
    date: '02.06.2026',
    label: 'Индивидуальные тесты',
    color: 'gold',
    title: 'Назначение другому сотруднику создает копию теста',
    changes: [
      'Если в строке индивидуального назначения выбрать другого сотрудника, старое назначение не пропадает.',
      'Система создает копию теста вместе с вопросами и ответами.',
      'Копия назначается новому сотруднику, а исходный тест остается у прошлого сотрудника.',
    ],
    instructions: [
      'Откройте вкладку "Тесты" и блок "Индивидуальные назначения".',
      'В колонке "Пользователь" выберите другого сотрудника.',
      'После сохранения появится новая строка назначения с копией теста.',
    ],
  },
  {
    date: '02.06.2026',
    label: 'Вопросы',
    color: 'cyan',
    title: 'Редактирование вопросов внутри индивидуальных тестов',
    changes: [
      'В строках индивидуальных назначений появился быстрый переход к вопросам теста.',
      'В редакторе можно менять список вопросов, тексты вопросов, изображения, баллы и варианты ответов.',
      'Изменения применяются к тесту из выбранной строки индивидуального назначения.',
    ],
    instructions: [
      'Откройте вкладку "Тесты" и блок "Индивидуальные назначения".',
      'В нужной строке нажмите "Вопросы и ответы".',
      'В открывшемся редакторе добавляйте, удаляйте или редактируйте вопросы и варианты ответов.',
    ],
  },
  {
    date: '03.06.2026',
    label: 'Статистика',
    color: 'geekblue',
    title: 'Полный рейтинг пользователей по среднему баллу',
    changes: [
      'Таблица больше не ограничена первыми 20 пользователями.',
      'В рейтинг попадают все пользователи, включая тех, у кого пока нет завершенных попыток.',
      'Добавлены сортировки и пагинация для длинного списка.',
    ],
    instructions: [
      'Откройте вкладку "Статистика".',
      'Нажмите "Обновить", если данные еще не загружены.',
      'В блоке "Все пользователи по среднему баллу" используйте сортировку по баллу, попыткам, сданным и несданным тестам.',
    ],
  },
  {
    date: '03.06.2026',
    label: 'Дашборды',
    color: 'magenta',
    title: 'Отдельная вкладка с диаграммами по статистике',
    changes: [
      'Добавлена вкладка "Дашборды" и прямой путь /admin/dashboards.',
      'Внутри есть три раздела: "Обзор", "Пользователи", "Тесты и вопросы".',
      'Региональный вариант дашборда не добавлялся.',
    ],
    instructions: [
      'Откройте вкладку "Дашборды" в админ-панели.',
      'Нажмите "Обновить дашборды", чтобы подтянуть свежую статистику и попытки.',
      'Переключайтесь между разделами "Обзор", "Пользователи", "Тесты и вопросы".',
    ],
  },
  {
    date: '03.06.2026',
    label: 'Карточки тестов',
    color: 'orange',
    title: 'Исправлено описание с неверным числом вопросов',
    changes: [
      'Некоторые тесты показывали в описании "Тест с 0 вопросами", хотя фактически вопросы в тесте были.',
      'Причина была в старом авто-описании теста, которое хранилось отдельно от реального количества вопросов.',
      'Теперь авто-описание пересчитывается по фактическому количеству вопросов.',
    ],
    instructions: [
      'Откройте страницу "Тесты" у пользователя.',
      'Сравните описание карточки и бейдж "Вопросов" — число должно совпадать.',
      'Если тест действительно без вопросов, он будет отображаться как "Тест без вопросов" и не должен запускаться.',
    ],
  },
  {
    date: '04.06.2026',
    label: 'Вопросы',
    color: 'volcano',
    title: 'Расширена статистика по вопросам',
    changes: [
      'В статистике появились общее количество вопросов и количество уникальных вопросов.',
      'В деталях проблемного вопроса теперь можно увидеть, кто отвечал на него и с каким результатом.',
      'Вопросы в просмотре больше не должны обрезаться, чтобы админ видел формулировку полностью.',
    ],
    instructions: [
      'Откройте вкладку "Статистика" или "Дашборды".',
      'В блоках с вопросами раскройте нужную строку или смотрите карточку вопроса.',
      'Используйте список отвечавших, чтобы понять, у каких сотрудников возникла ошибка.',
    ],
  },
  {
    date: '04.06.2026',
    label: 'Изображения',
    color: 'lime',
    title: 'Восстановлены изображения и порядок вопросов',
    changes: [
      'Для тестов с изображениями исправлены пути к картинкам.',
      'В редакторе вопросов исправлено отображение нумерации.',
      'Кнопка "Назад" в тестах возвращает на логичную предыдущую страницу, а не на новости.',
    ],
    instructions: [
      'Откройте вкладку "Тесты".',
      'Перейдите в "Управление вопросами" у теста с изображениями.',
      'Проверьте, что изображения открываются, а номера вопросов идут по порядку.',
    ],
  },
  {
    date: '04.06.2026',
    label: 'Интерфейс',
    color: 'blue',
    title: 'Расширены окна статистики и тестов',
    changes: [
      'Страницы статистики, тестов и управления вопросами стали шире.',
      'В таблицах стало удобнее смотреть длинные названия тестов и вопросов.',
      'Исправлена работа выбора количества строк на странице в статистике.',
    ],
    instructions: [
      'Откройте вкладку "Статистика" или "Тесты".',
      'Используйте выбор "20 / 50 / 100 / 200" строк на странице.',
      'Для широких таблиц используйте горизонтальную прокрутку внутри таблицы.',
    ],
  },
  {
    date: '11.06.2026',
    label: 'Поиск',
    color: 'green',
    title: 'Добавлен поиск по тестам',
    changes: [
      'Во вкладке "Тесты" появилось поле поиска над таблицей управления тестами.',
      'Поиск работает по названию, блоку, категории, описанию, количеству вопросов и времени.',
      'Рядом с поиском показывается счетчик найденных тестов.',
    ],
    instructions: [
      'Откройте вкладку "Тесты".',
      'Введите часть названия, блока или категории в поле поиска.',
      'Очистите поле крестиком, чтобы снова увидеть полный список тестов.',
    ],
  },
  {
    date: '11.06.2026',
    label: 'Дашборды',
    color: 'magenta',
    title: 'Дашборды показывают всех пользователей и вопросы со 100% ошибками',
    changes: [
      'В разделе "Пользователи" больше нет ограничения на первые 10-12 строк.',
      'Рейтинг, попытки и "сдано / не сдано" строятся по всем пользователям.',
      'В разделе "Тесты и вопросы" отдельно выводятся все вопросы, где 100% ответов были ошибочными.',
    ],
    instructions: [
      'Откройте вкладку "Дашборды".',
      'В разделе "Пользователи" просмотрите полный список сотрудников.',
      'В разделе "Тесты и вопросы" смотрите блок "Вопросы со 100% ошибками" и тепловую карту.',
    ],
  },
];

const parseAdminNewsDate = (date: string) => {
  const [day, month, year] = date.split('.').map(Number);
  if (!day || !month || !year) return 0;
  return new Date(year, month - 1, day).getTime();
};

const getAdminNewsTags = (item: AdminNewsItem) => (
  item.tags?.length
    ? item.tags
    : [{ label: item.label, color: item.color }]
);

const adminNewsTagOptions = Array.from(
  new Set(
    adminNewsItems.flatMap((item) =>
      getAdminNewsTags(item).map((tag) => tag.label)
    )
  )
)
  .sort((a, b) => a.localeCompare(b, 'ru'))
  .map((label) => ({ label, value: label }));

const sortedAdminNewsItems = adminNewsItems
  .map((item, index) => ({ item, index }))
  .sort((a, b) => parseAdminNewsDate(b.item.date) - parseAdminNewsDate(a.item.date) || a.index - b.index)
  .map(({ item }) => item);

const AdminPanel: React.FC<AdminPanelProps> = ({ initialTab = 'news' }) => {
  const { t } = useLanguage();
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [selectedAdminNewsTags, setSelectedAdminNewsTags] = useState<string[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [isUserStructureExpanded, setIsUserStructureExpanded] = useState(() => {
    try {
      return window.localStorage.getItem(USER_STRUCTURE_STORAGE_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const [selectedUserDepartment, setSelectedUserDepartment] = useState(ALL_USER_DEPARTMENTS);
  const [userDirectorySearch, setUserDirectorySearch] = useState('');
  const [userActivityFilter, setUserActivityFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [transferUser, setTransferUser] = useState<any | null>(null);
  const [transferDepartment, setTransferDepartment] = useState<string>();
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [updatingUserActivity, setUpdatingUserActivity] = useState<number | null>(null);
  const [tests, setTests] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [detailedStats, setDetailedStats] = useState<any>(null);
  const [statisticsView, setStatisticsView] = useState('details');
  const [loadingStats, setLoadingStats] = useState(false);
  const [questionStatsTestId, setQuestionStatsTestId] = useState<number | undefined>();
  const [questionStatsDateRange, setQuestionStatsDateRange] = useState<any>(null);
  const [questionStatsTopSize, setQuestionStatsTopSize] = useState<10 | 15 | 20>(10);
  const [problemQuestionsOpen, setProblemQuestionsOpen] = useState(false);
  const [attempts, setAttempts] = useState<any[]>([]);
  const [attemptUserSearch, setAttemptUserSearch] = useState('');
  const [attemptDepartment, setAttemptDepartment] = useState<string>();
  const [attemptTestId, setAttemptTestId] = useState<number>();
  const [attemptDateRange, setAttemptDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [selectedAttemptIds, setSelectedAttemptIds] = useState<number[]>([]);
  const [userStatsPagination, setUserStatsPagination] = useState({ current: 1, pageSize: 50 });
  const [attemptsPagination, setAttemptsPagination] = useState({ current: 1, pageSize: 20 });
  const [userQuestionsModalUser, setUserQuestionsModalUser] = useState<any | null>(null);
  const [userAttemptsModalUser, setUserAttemptsModalUser] = useState<any | null>(null);
  const [userAttemptsModalItems, setUserAttemptsModalItems] = useState<any[]>([]);
  const [loadingUserAttempts, setLoadingUserAttempts] = useState(false);
  const userRanking = useUserStatisticsPeriod(initialTab === 'statistics');
  const { expandedRows: expandedUserRows, details: userDetails, loadingDetails: loadingUserDetails } = userRanking;
  const changeUserStatsPeriod = (period: StatisticsPeriod) => {
    userRanking.changePeriod(period);
    setUserStatsPagination(previous => ({ ...previous, current: 1 }));
  };
  const rankingRange = userRanking.period
    ? [dayjs(userRanking.period.from), dayjs(userRanking.period.to)] as [dayjs.Dayjs, dayjs.Dayjs]
    : null;
  const rankingMonth = rankingRange
    && rankingRange[0].format('YYYY-MM-DD') === rankingRange[0].startOf('month').format('YYYY-MM-DD')
    && rankingRange[1].format('YYYY-MM-DD') === rankingRange[0].endOf('month').format('YYYY-MM-DD')
    ? rankingRange[0] : null;
  const [unansweredQuestionsByUser, setUnansweredQuestionsByUser] = useState<Record<number, any>>({});
  const [loadingUnansweredQuestionsByUser, setLoadingUnansweredQuestionsByUser] = useState<Record<number, boolean>>({});
  const [selectedUnansweredQuestionIdsByUser, setSelectedUnansweredQuestionIdsByUser] = useState<Record<number, React.Key[]>>({});
  const [assigningUnansweredQuestionsByUser, setAssigningUnansweredQuestionsByUser] = useState<Record<number, boolean>>({});
  const [unansweredQuestionSearchByUser, setUnansweredQuestionSearchByUser] = useState<Record<number, string>>({});
  const [unansweredTestModalUser, setUnansweredTestModalUser] = useState<any | null>(null);
  const [unansweredTestTitle, setUnansweredTestTitle] = useState('');
  const [expandedTestRows, setExpandedTestRows] = useState<Set<number>>(new Set());
  const [testDetails, setTestDetails] = useState<Record<number, any>>({});
  const [loadingTestDetails, setLoadingTestDetails] = useState<Record<number, boolean>>({});
  const [expandedQuestionRows, setExpandedQuestionRows] = useState<Set<number>>(new Set());
  const [questionDetails, setQuestionDetails] = useState<Record<number, any>>({});
  const [loadingQuestionDetails, setLoadingQuestionDetails] = useState<Record<number, boolean>>({});

  const attemptDateFrom = attemptDateRange?.[0]?.format('YYYY-MM-DD');
  const attemptDateTo = attemptDateRange?.[1]?.format('YYYY-MM-DD');

  const filteredAttempts = useMemo(() => {
    const searchTerms = normalizeUserDirectoryValue(attemptUserSearch).replace(/ё/g, 'е').split(/\s+/).filter(Boolean);
    return attempts.filter((attempt) => {
      const name = normalizeUserDirectoryValue(attempt.user_name).replace(/ё/g, 'е');
      const completedDate = attempt.completed_at
        ? dayjs.utc(attempt.completed_at).tz('Europe/Moscow').format('YYYY-MM-DD')
        : undefined;
      return searchTerms.every((term) => name.includes(term))
        && (attemptDepartment === undefined || String(attempt.user_department || '').trim() === attemptDepartment)
        && (attemptTestId === undefined || attempt.test_id === attemptTestId)
        && (!attemptDateFrom || (completedDate !== undefined && completedDate >= attemptDateFrom))
        && (!attemptDateTo || (completedDate !== undefined && completedDate <= attemptDateTo));
    });
  }, [attempts, attemptUserSearch, attemptDepartment, attemptTestId, attemptDateFrom, attemptDateTo]);

  const attemptDepartmentOptions = useMemo(() => Array.from(
    new Set(attempts.map((attempt) => String(attempt.user_department || '').trim()))
  ).sort((a, b) => a.localeCompare(b, 'ru')).map((department) => ({
    value: department, label: department || 'Без отдела',
  })), [attempts]);

  const attemptTestOptions = useMemo(() => {
    const byId = new Map<number, string>();
    attempts.forEach((attempt) => byId.set(attempt.test_id, attempt.test_title || `Тест #${attempt.test_id}`));
    const titleCounts = new Map<string, number>();
    byId.forEach((title) => titleCounts.set(title, (titleCounts.get(title) || 0) + 1));
    return Array.from(byId, ([id, title]) => ({
      value: id, label: titleCounts.get(title)! > 1 ? `${title} · #${id}` : title,
    })).sort((a, b) => a.label.localeCompare(b.label, 'ru'));
  }, [attempts]);

  const hasAttemptFilters = Boolean(attemptUserSearch.trim() || attemptDateFrom || attemptDateTo)
    || attemptDepartment !== undefined || attemptTestId !== undefined;
  const resetAttemptSelection = () => {
    setAttemptsPagination((prev) => ({ ...prev, current: 1 }));
    setSelectedAttemptIds([]);
  };

  const filteredAdminNewsItems = useMemo(() => {
    if (!selectedAdminNewsTags.length) {
      return sortedAdminNewsItems;
    }

    return sortedAdminNewsItems.filter((item) => {
      const itemTags = new Set(getAdminNewsTags(item).map((tag) => tag.label));
      return selectedAdminNewsTags.every((tag) => itemTags.has(tag));
    });
  }, [selectedAdminNewsTags]);

  const adminTabRoutes: Record<string, string> = {
    news: '/admin/news',
    dashboards: '/admin/dashboards',
    users: '/admin/users',
    tests: '/admin/board',  // каталог тестов переехал в рабочий стол
    statistics: '/admin/statistics',
    mail: '/admin/mail',
  };

  const directoryUsers = useMemo(() => users.filter((user) => (
    userActivityFilter === 'all'
      || (userActivityFilter === 'active' ? user.is_active : !user.is_active)
  )), [users, userActivityFilter]);

  const userDepartments = useMemo(() => {
    const grouped = new Map<string, any[]>();
    directoryUsers.forEach((user) => {
      const department = getUserDepartment(user);
      const departmentUsers = grouped.get(department) || [];
      departmentUsers.push(user);
      grouped.set(department, departmentUsers);
    });

    return Array.from(grouped.entries())
      .map(([name, departmentUsers]) => ({
        name,
        users: departmentUsers.sort((a, b) =>
          String(a.full_name || '').localeCompare(String(b.full_name || ''), 'ru')
        ),
      }))
      .sort((a, b) => {
        if (a.name === 'Администрация') return -1;
        if (b.name === 'Администрация') return 1;
        if (a.name === 'Без отдела') return 1;
        if (b.name === 'Без отдела') return -1;
        return a.name.localeCompare(b.name, 'ru');
      });
  }, [directoryUsers]);

  const transferDepartmentOptions = useMemo(() => (
    Array.from(new Set(users.map(getUserDepartment)))
      .filter((name) => name !== 'Без отдела')
      .sort((a, b) => a.localeCompare(b, 'ru'))
      .map((name) => ({ label: name, value: name }))
      .concat({ label: 'Без отдела', value: '' })
  ), [users]);

  useEffect(() => {
    if (selectedUserDepartment !== ALL_USER_DEPARTMENTS
      && !userDepartments.some((department) => department.name === selectedUserDepartment)) {
      setSelectedUserDepartment(ALL_USER_DEPARTMENTS);
    }
  }, [userDepartments, selectedUserDepartment]);

  const filteredUserDepartments = useMemo(() => {
    const query = normalizeUserDirectoryValue(userDirectorySearch);
    if (!query) return userDepartments;

    return userDepartments
      .map((department) => ({
        ...department,
        users: department.users.filter((user) => normalizeUserDirectoryValue([
          user.full_name,
          user.email,
          user.position,
          user.region,
          department.name,
        ].filter(Boolean).join(' ')).includes(query)),
      }))
      .filter((department) => department.users.length > 0);
  }, [userDepartments, userDirectorySearch]);

  const visibleDirectoryUsers = useMemo(() => {
    if (selectedUserDepartment === ALL_USER_DEPARTMENTS) {
      return filteredUserDepartments.flatMap((department) => department.users);
    }
    return filteredUserDepartments.find((department) => department.name === selectedUserDepartment)?.users || [];
  }, [filteredUserDepartments, selectedUserDepartment]);

  const visibleDirectoryTitle = selectedUserDepartment === ALL_USER_DEPARTMENTS
    ? (userDirectorySearch.trim() ? 'Результаты поиска' : 'Все сотрудники')
    : selectedUserDepartment;

  useEffect(() => {
    try {
      window.localStorage.setItem(USER_STRUCTURE_STORAGE_KEY, String(isUserStructureExpanded));
    } catch {
      // localStorage может быть отключён политиками браузера — сворачивание всё равно работает в текущей сессии.
    }
  }, [isUserStructureExpanded]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get('/admin/users');
      setUsers(response.data);
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
    }
  }, []);

  const fetchTests = useCallback(async () => {
    try {
      const response = await api.get('/admin/tests');
      setTests(response.data || []);
    } catch (error) {
      console.error('Ошибка загрузки тестов:', error);
      setTests([]);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/admin/statistics/overview');
      setStats(response.data);
    } catch (error) {
      console.error('Ошибка загрузки статистики:', error);
    }
  }, []);

  const fetchDetailedStats = useCallback(async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/admin/statistics/detailed', {
        params: {
          test_id: questionStatsTestId,
          date_from: questionStatsDateRange?.[0]?.format('YYYY-MM-DD'),
          date_to: questionStatsDateRange?.[1]?.format('YYYY-MM-DD'),
        },
      });
      setDetailedStats(response.data);
      setUserStatsPagination((prev) => ({ ...prev, current: 1 }));
    } catch (error) {
      console.error('Ошибка загрузки детальной статистики:', error);
      message.error('Не удалось загрузить детальную статистику');
    } finally {
      setLoadingStats(false);
    }
  }, [questionStatsDateRange, questionStatsTestId]);

  const fetchAttempts = useCallback(async () => {
    setLoadingAttempts(true);
    try {
      const response = await api.get('/admin/attempts?limit=1000');
      setAttempts(response.data.attempts || []);
      setAttemptsPagination((prev) => ({ ...prev, current: 1 }));
    } catch (error) {
      console.error('Ошибка загрузки попыток:', error);
      message.error('Не удалось загрузить попытки');
    } finally {
      setLoadingAttempts(false);
    }
  }, []);

  const fetchUserAttempts = useCallback(async (userId: number) => {
    setLoadingUserAttempts(true);
    try {
      const response = await api.get('/admin/attempts', {
        params: { user_id: userId, limit: 1000 },
      });
      setUserAttemptsModalItems(response.data.attempts || []);
    } catch (error: any) {
      console.error('Ошибка загрузки прохождений сотрудника:', error);
      message.error(getApiErrorMessage(error, 'Не удалось загрузить прохождения сотрудника'));
      setUserAttemptsModalItems([]);
    } finally {
      setLoadingUserAttempts(false);
    }
  }, []);



  useEffect(() => {
    fetchUsers();
    fetchTests();
    fetchStats();
  }, [fetchUsers, fetchTests, fetchStats]);

  useEffect(() => {
    setActiveTab(initialTab);
    if (initialTab === 'statistics') {
      fetchDetailedStats();
      fetchAttempts();
    }
    if (initialTab === 'dashboards') {
      fetchDetailedStats();
      fetchAttempts();
    }
    if (initialTab === 'tests') {
      fetchAttempts();
    }
  }, [initialTab, fetchDetailedStats, fetchAttempts]);


  const fetchTestDetails = useCallback(async (attemptId: number) => {
    if (testDetails[attemptId]) {
      // Данные уже загружены
      const newExpanded = new Set(expandedTestRows);
      if (newExpanded.has(attemptId)) {
        newExpanded.delete(attemptId);
      } else {
        newExpanded.add(attemptId);
      }
      setExpandedTestRows(newExpanded);
      return;
    }

    setLoadingTestDetails(prev => ({ ...prev, [attemptId]: true }));
    try {
      const response = await api.get(`/admin/attempts/${attemptId}/details`);
      setTestDetails(prev => ({ ...prev, [attemptId]: response.data }));
      setExpandedTestRows(prev => new Set(prev).add(attemptId));
    } catch (error) {
      console.error('Ошибка загрузки детальной информации о тесте:', error);
      message.error('Не удалось загрузить детальную информацию о тесте');
    } finally {
      setLoadingTestDetails(prev => ({ ...prev, [attemptId]: false }));
    }
  }, [testDetails, expandedTestRows]);

  const fetchQuestionDetails = useCallback(async (questionId: number) => {
    if (questionDetails[questionId]) {
      // Данные уже загружены
      const newExpanded = new Set(expandedQuestionRows);
      if (newExpanded.has(questionId)) {
        newExpanded.delete(questionId);
      } else {
        newExpanded.add(questionId);
      }
      setExpandedQuestionRows(newExpanded);
      return;
    }

    setLoadingQuestionDetails(prev => ({ ...prev, [questionId]: true }));
    try {
      const response = await api.get(`/admin/questions/${questionId}/details`);
      setQuestionDetails(prev => ({ ...prev, [questionId]: response.data }));
      setExpandedQuestionRows(prev => new Set(prev).add(questionId));
    } catch (error) {
      console.error('Ошибка загрузки детальной информации о вопросе:', error);
      message.error('Не удалось загрузить детальную информацию о вопросе');
    } finally {
      setLoadingQuestionDetails(prev => ({ ...prev, [questionId]: false }));
    }
  }, [questionDetails, expandedQuestionRows]);

  const handleDeleteAttempt = (attemptRecord: any) => {
    const attemptId = Number(
      typeof attemptRecord === 'number' ? attemptRecord : attemptRecord?.id
    );
    if (!Number.isFinite(attemptId)) return;

    const employeeName = attemptRecord?.user_name || userAttemptsModalUser?.full_name;
    const testTitle = attemptRecord?.test_title;
    const completedAt = attemptRecord?.completed_at || attemptRecord?.started_at;
    Modal.confirm({
      title: 'Удалить прохождение теста?',
      content: (
        <Space direction="vertical" size={6}>
          {employeeName && <Typography.Text>Сотрудник: <strong>{employeeName}</strong></Typography.Text>}
          {testTitle && <Typography.Text>Тест: <strong>{testTitle}</strong></Typography.Text>}
          {completedAt && (
            <Typography.Text>
              Дата: <strong>{dayjs.utc(completedAt).tz(getTimezoneByRegion(attemptRecord?.user_region)).format('DD.MM.YYYY HH:mm')}</strong>
            </Typography.Text>
          )}
          <Typography.Text type="danger">
            Ответы и результат будут удалены без возможности восстановления. Если это последнее завершённое прохождение, назначение снова откроется сотруднику.
          </Typography.Text>
        </Space>
      ),
      okText: 'Удалить прохождение',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/attempts/${attemptId}`);
          message.success(
            response.data?.assignment_reopened
              ? 'Прохождение удалено. Тест снова доступен сотруднику.'
              : 'Прохождение теста удалено.'
          );
          setAttempts(prev => prev.filter(item => Number(item.id) !== attemptId));
          setUserAttemptsModalItems(prev => prev.filter(item => Number(item.id) !== attemptId));
          setSelectedAttemptIds(prev => prev.filter(id => id !== attemptId));
          setExpandedTestRows(prev => {
            const next = new Set(prev);
            next.delete(attemptId);
            return next;
          });
          setTestDetails(prev => {
            const next = { ...prev };
            delete next[attemptId];
            return next;
          });
          fetchAttempts();
          fetchStats();
          fetchDetailedStats();
        } catch (error: any) {
          message.error(getApiErrorMessage(error, 'Ошибка удаления прохождения'));
        }
      },
    });
  };

  const handleDeleteAllAttempts = () => {
    Modal.confirm({
      title: 'Удаление всех попыток',
      content: 'Вы уверены, что хотите удалить ВСЕ попытки прохождения тестов? Это действие нельзя отменить и удалит всю статистику.',
      okText: 'Удалить все',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const response = await api.delete('/admin/attempts');
          message.success(`Удалено попыток: ${response.data.deleted_count}`);
          setSelectedAttemptIds([]);
          setExpandedTestRows(new Set());
          setTestDetails({});
          fetchAttempts();
          fetchStats();
          fetchDetailedStats();
        } catch (error: any) {
          message.error(getApiErrorMessage(error, 'Ошибка удаления попыток'));
        }
      },
    });
  };

  const handleDeleteSelectedAttempts = () => {
    if (selectedAttemptIds.length === 0) {
      message.warning('Выберите попытки для удаления');
      return;
    }

    Modal.confirm({
      title: 'Удаление выбранных попыток',
      content: `Удалить выбранные попытки: ${selectedAttemptIds.length}? Это действие нельзя отменить.`,
      okText: 'Удалить выбранные',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          const response = await api.post('/admin/attempts/delete-selected', {
            attempt_ids: selectedAttemptIds,
          });
          message.success(`Удалено попыток: ${response.data.deleted_count}`);
          setSelectedAttemptIds([]);
          setExpandedTestRows(prev => {
            const next = new Set(prev);
            selectedAttemptIds.forEach(id => next.delete(id));
            return next;
          });
          setTestDetails(prev => {
            const next = { ...prev };
            selectedAttemptIds.forEach(id => delete next[id]);
            return next;
          });
          fetchAttempts();
          fetchStats();
          fetchDetailedStats();
        } catch (error: any) {
          message.error(getApiErrorMessage(error, 'Ошибка удаления выбранных попыток'));
        }
      },
    });
  };

  const [userModalVisible, setUserModalVisible] = useState(false);
  const [userProfileImportModalVisible, setUserProfileImportModalVisible] = useState(false);
  const [userProfileImportFile, setUserProfileImportFile] = useState<any>(null);
  const [userProfileImportSubmitting, setUserProfileImportSubmitting] = useState(false);
  const [userFormData, setUserFormData] = useState({
    username: '',
    email: '',
    first_name: '',
    last_name: '',
    password: '',
    department: '',
    position: '',
    work_experience: '',
    region: '',
    role: 'employee' as 'employee' | 'admin'
  });
  const [userSubmitting, setUserSubmitting] = useState(false);
  const [userProfileModalVisible, setUserProfileModalVisible] = useState(false);
  const [editingUserProfile, setEditingUserProfile] = useState<any | null>(null);
  const [userProfileFormData, setUserProfileFormData] = useState({
    department: '',
    position: '',
    work_experience: '',
    region: '',
  });
  const [userProfileSubmitting, setUserProfileSubmitting] = useState(false);
















  const handleUserProfileImport = useCallback(async () => {
    if (!userProfileImportFile) {
      message.warning('Выберите Excel файл для импорта');
      return;
    }

    const formData = new FormData();
    formData.append('file', userProfileImportFile);

    setUserProfileImportSubmitting(true);
    try {
      const response = await api.post('/admin/users/import-profile-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      const result = response.data || {};
      const warnings = Array.isArray(result.warnings) ? result.warnings : [];

      message.success(result.message || 'Импорт данных сотрудников завершен');
      setUserProfileImportModalVisible(false);
      setUserProfileImportFile(null);
      fetchUsers();
      fetchDetailedStats();
      fetchAttempts();

      if (warnings.length > 0) {
        Modal.warning({
          title: 'Импорт завершен с предупреждениями',
          width: 720,
          content: (
            <Space direction="vertical" style={{ width: '100%' }} size="small">
              <Text>Обновлено: {result.updated || 0}; пропущено: {result.skipped || 0}; без изменений: {result.unchanged || 0}.</Text>
              <ul style={{ margin: 0, paddingLeft: 20, maxHeight: 260, overflow: 'auto' }}>
                {warnings.map((warning: string) => (
                  <li key={warning}>{warning}</li>
                ))}
              </ul>
              {result.warnings_truncated && (
                <Text type="secondary">Показаны первые предупреждения, полный список сокращен.</Text>
              )}
            </Space>
          ),
        });
      }
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка импорта данных сотрудников'));
    } finally {
      setUserProfileImportSubmitting(false);
    }
  }, [userProfileImportFile, fetchUsers, fetchDetailedStats, fetchAttempts]);

  const userProfileImportUploadProps: UploadProps = {
    name: 'file',
    accept: '.xlsx,.xls',
    beforeUpload: (file) => {
      setUserProfileImportFile(file);
      setUserProfileImportModalVisible(true);
      return false;
    },
    showUploadList: false,
  };

  const openUserProfileModal = (user: any) => {
    setEditingUserProfile(user);
    setUserProfileFormData({
      department: user.department || '',
      position: user.position || '',
      work_experience: user.work_experience || '',
      region: user.region || '',
    });
    setUserProfileModalVisible(true);
  };

  const closeUserProfileModal = () => {
    setUserProfileModalVisible(false);
    setEditingUserProfile(null);
    setUserProfileFormData({
      department: '',
      position: '',
      work_experience: '',
      region: '',
    });
  };

  const handleUserProfileSave = async () => {
    if (!editingUserProfile) {
      return;
    }

    setUserProfileSubmitting(true);
    try {
      const response = await api.put(`/admin/users/${editingUserProfile.id}/profile`, userProfileFormData);
      setUsers((prev) =>
        prev.map((user) => (user.id === editingUserProfile.id ? response.data : user))
      );
      message.success('Данные сотрудника обновлены');
      closeUserProfileModal();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка обновления данных сотрудника'));
    } finally {
      setUserProfileSubmitting(false);
    }
  };

  const handleUserTransfer = async () => {
    if (!transferUser || transferDepartment === undefined) return;
    setTransferSubmitting(true);
    try {
      const response = await api.put(`/admin/users/${transferUser.id}/profile`, {
        department: transferDepartment,
      });
      setUsers((prev) => prev.map((user) => user.id === transferUser.id ? response.data : user));
      setSelectedUserDepartment(getUserDepartment(response.data));
      setUserDirectorySearch('');
      setTransferUser(null);
      message.success('Сотрудник переведён в другой отдел');
      fetchDetailedStats();
      fetchAttempts();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Не удалось перевести сотрудника'));
    } finally {
      setTransferSubmitting(false);
    }
  };

  const handleUserActivityChange = (user: any) => {
    const isActive = !user.is_active;
    Modal.confirm({
      title: isActive ? 'Активировать сотрудника?' : 'Деактивировать сотрудника?',
      content: (
        <Space direction="vertical">
          <Text strong>{user.full_name || user.email}</Text>
          <Text>{isActive
            ? 'Сотрудник снова сможет входить в FEBEST и проходить назначенные тесты.'
            : 'Сотрудник потеряет доступ к FEBEST. Рабочие данные, назначения и история прохождений сохранятся. Восстановить доступ можно через фильтр «Неактивные».'}</Text>
        </Space>
      ),
      okText: isActive ? 'Активировать' : 'Деактивировать',
      okType: isActive ? 'primary' : 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        setUpdatingUserActivity(user.id);
        try {
          const response = await api.put(`/admin/users/${user.id}/activity`, { is_active: isActive });
          setUsers((prev) => prev.map((item) => item.id === user.id ? response.data : item));
          message.success(isActive ? 'Сотрудник активирован' : 'Сотрудник деактивирован');
        } catch (error: any) {
          message.error(getApiErrorMessage(error, 'Не удалось изменить статус сотрудника'));
          throw error;
        } finally {
          setUpdatingUserActivity(null);
        }
      },
    });
  };

  const fetchUserUnansweredQuestions = async (userId: number) => {
    setLoadingUnansweredQuestionsByUser((prev) => ({ ...prev, [userId]: true }));
    try {
      const response = await api.get(`/admin/users/${userId}/unanswered-questions`);
      setUnansweredQuestionsByUser((prev) => ({
        ...prev,
        [userId]: response.data,
      }));
      setSelectedUnansweredQuestionIdsByUser((prev) => ({
        ...prev,
        [userId]: (prev[userId] || []).filter((questionId) =>
          (response.data?.questions || []).some(
            (question: any) =>
              Number(question.question_id) === Number(questionId)
              && !question.already_assigned
          )
        ),
      }));
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Не удалось загрузить непройденные вопросы'));
    } finally {
      setLoadingUnansweredQuestionsByUser((prev) => ({ ...prev, [userId]: false }));
    }
  };

  const openUserQuestionsPanel = (user: any) => {
    setUserQuestionsModalUser(user);
    if (!unansweredQuestionsByUser[user.id]) {
      fetchUserUnansweredQuestions(user.id);
    }
  };

  const openUserAttemptsModal = (user: any) => {
    setUserAttemptsModalUser(user);
    setUserAttemptsModalItems([]);
    fetchUserAttempts(user.id);
  };

  const closeUnansweredTestModal = () => {
    setUnansweredTestModalUser(null);
    setUnansweredTestTitle('');
  };

  const handleCreateUnansweredQuestionsTest = async () => {
    const user = unansweredTestModalUser;
    if (!user) return;

    const title = unansweredTestTitle.trim();
    if (!title) {
      message.warning('Введите название теста');
      return;
    }

    const questionIds = (selectedUnansweredQuestionIdsByUser[user.id] || [])
      .map((questionId) => Number(questionId))
      .filter((questionId) => Number.isFinite(questionId));
    if (!questionIds.length) {
      message.warning('Выберите хотя бы один вопрос');
      closeUnansweredTestModal();
      return;
    }

    setAssigningUnansweredQuestionsByUser((prev) => ({ ...prev, [user.id]: true }));
    try {
      const response = await api.post(
        `/admin/users/${user.id}/unanswered-questions/assign`,
        {
          question_ids: questionIds,
          title,
        }
      );
      message.success(
        `Тест «${response.data.created?.[0]?.test_title || title}» создан: `
        + `${response.data.questions_assigned} вопросов`
      );
      setSelectedUnansweredQuestionIdsByUser((prev) => ({ ...prev, [user.id]: [] }));
      closeUnansweredTestModal();
      await fetchUserUnansweredQuestions(user.id);
      fetchTests();
    } catch (error: any) {
      message.error(getApiErrorMessage(error, 'Ошибка назначения непройденных вопросов'));
    } finally {
      setAssigningUnansweredQuestionsByUser((prev) => ({ ...prev, [user.id]: false }));
    }
  };

  const openUnansweredTestModal = (user: any) => {
    const questionIds = (selectedUnansweredQuestionIdsByUser[user.id] || [])
      .map((questionId) => Number(questionId))
      .filter((questionId) => Number.isFinite(questionId));
    if (!questionIds.length) {
      message.warning('Выберите хотя бы один вопрос');
      return;
    }

    setUnansweredTestModalUser(user);
    setUnansweredTestTitle(`Непройденные вопросы — ${user.full_name}`);
  };



  const renderUserUnansweredQuestions = (user: any) => {
    if (loadingUnansweredQuestionsByUser[user.id]) {
      return (
        <div style={{ padding: 24, textAlign: 'center' }}>
          <Spin />
        </div>
      );
    }

    const payload = unansweredQuestionsByUser[user.id];
    if (!payload) {
      return (
        <Button onClick={() => fetchUserUnansweredQuestions(user.id)}>
          Загрузить непройденные вопросы
        </Button>
      );
    }

    const searchQuery = (unansweredQuestionSearchByUser[user.id] || '')
      .trim()
      .toLocaleLowerCase('ru');
    const questions = (payload.questions || []).filter((question: any) => {
      if (!searchQuery) return true;
      return [
        question.text,
        question.source_test_title,
        question.source_test_subgroup,
        question.block,
      ].some((field) =>
        String(field || '').toLocaleLowerCase('ru').includes(searchQuery)
      );
    });
    const selectedRowKeys = selectedUnansweredQuestionIdsByUser[user.id] || [];

    return (
      <Card
        size="small"
        title={`Непройденные вопросы: ${payload.total}`}
        extra={
          <Space wrap>
            <Text type="secondary">
              Доступно: {payload.available_to_assign}; уже назначено: {payload.already_assigned}
            </Text>
            <Button
              onClick={() => fetchUserUnansweredQuestions(user.id)}
              loading={Boolean(loadingUnansweredQuestionsByUser[user.id])}
            >
              Обновить
            </Button>
            <Button
              type="primary"
              disabled={!selectedRowKeys.length}
              loading={Boolean(assigningUnansweredQuestionsByUser[user.id])}
              onClick={() => openUnansweredTestModal(user)}
            >
              Собрать тест ({selectedRowKeys.length})
            </Button>
          </Space>
        }
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Список составлен по сохранённым ответам сотрудника"
          description="Одинаковые вопросы из разных тестов показаны один раз. Выбранные вопросы войдут в новый тест отдельными вопросами."
        />
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Поиск по вопросу, тесту или категории"
          value={unansweredQuestionSearchByUser[user.id] || ''}
          onChange={(event) =>
            setUnansweredQuestionSearchByUser((prev) => ({
              ...prev,
              [user.id]: event.target.value,
            }))
          }
          style={{ maxWidth: 520, marginBottom: 12 }}
        />
        {questions.length ? (
          <Table
            size="small"
            rowKey="question_id"
            dataSource={questions}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              pageSizeOptions: ['10', '25', '50', '100'],
              showTotal: (total) => `Вопросов: ${total}`,
            }}
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) =>
                setSelectedUnansweredQuestionIdsByUser((prev) => ({
                  ...prev,
                  [user.id]: keys,
                })),
              getCheckboxProps: (record: any) => ({
                disabled: Boolean(record.already_assigned),
              }),
            }}
            columns={[
              {
                title: 'Вопрос',
                dataIndex: 'text',
                key: 'text',
                render: (text: string) => (
                  <Text style={{ whiteSpace: 'normal' }}>{text}</Text>
                ),
              },
              {
                title: 'Исходный тест',
                dataIndex: 'source_test_title',
                key: 'source_test_title',
                width: 260,
              },
              {
                title: 'Блок',
                dataIndex: 'block',
                key: 'block',
                width: 120,
              },
              {
                title: 'Категория',
                dataIndex: 'source_test_subgroup',
                key: 'source_test_subgroup',
                width: 180,
                render: (value: string) => value || <Text type="secondary">—</Text>,
              },
              {
                title: 'Статус',
                key: 'status',
                width: 190,
                render: (_: unknown, record: any) =>
                  record.already_assigned ? (
                    <Tag color="blue" title={(record.active_assignment_titles || []).join(', ')}>
                      Уже назначен
                    </Tag>
                  ) : (
                    <Tag color="green">Можно назначить</Tag>
                  ),
              },
            ]}
            scroll={{ x: 1050 }}
          />
        ) : (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchQuery ? 'По запросу ничего не найдено' : 'Все вопросы пройдены'}
          />
        )}
      </Card>
    );
  };




















  const dashboardStats = useMemo(() => {
    const topUsers = detailedStats?.top_users || [];
    const problemQuestions = detailedStats?.problem_questions || [];
    const completedAttempts = attempts.filter((attempt) => attempt.is_completed);
    const countedAttempts = completedAttempts.filter((attempt) => attempt.is_counted === true);
    const passedAttempts = countedAttempts.filter((attempt) => attempt.is_passed).length;
    const failedAttempts = Math.max(0, countedAttempts.length - passedAttempts);

    const attemptsByDate = new Map<string, { date: string; attempts: number; scoreSum: number; scored: number }>();
    countedAttempts.forEach((attempt) => {
      const rawDate = attempt.completed_at || attempt.started_at;
      if (!rawDate) return;
      const dateKey = dayjs.utc(rawDate).format('YYYY-MM-DD');
      const current = attemptsByDate.get(dateKey) || {
        date: dateKey,
        attempts: 0,
        scoreSum: 0,
        scored: 0,
      };
      current.attempts += 1;
      if (typeof attempt.score === 'number') {
        current.scoreSum += attempt.score;
        current.scored += 1;
      }
      attemptsByDate.set(dateKey, current);
    });

    const daily = Array.from(attemptsByDate.values())
      .sort((a, b) => a.date.localeCompare(b.date))
      .slice(-14)
      .map((item) => ({
        ...item,
        label: dayjs(item.date).format('DD.MM'),
        average_score: item.scored ? Number((item.scoreSum / item.scored).toFixed(2)) : 0,
      }));

    const attemptsByTest = new Map<number, { test_title: string; attempts: number; passed: number; scoreSum: number; scored: number }>();
    countedAttempts.forEach((attempt) => {
      const title = attempt.test_title || 'Без названия';
      const current = attemptsByTest.get(attempt.test_id) || {
        test_title: title,
        attempts: 0,
        passed: 0,
        scoreSum: 0,
        scored: 0,
      };
      current.attempts += 1;
      if (attempt.is_passed) current.passed += 1;
      if (typeof attempt.score === 'number') {
        current.scoreSum += attempt.score;
        current.scored += 1;
      }
      attemptsByTest.set(attempt.test_id, current);
    });

    const testsPerformance = Array.from(attemptsByTest.values())
      .map((item) => ({
        ...item,
        failed: Math.max(0, item.attempts - item.passed),
        average_score: item.scored ? Number((item.scoreSum / item.scored).toFixed(2)) : 0,
        pass_rate: item.attempts ? Number(((item.passed / item.attempts) * 100).toFixed(2)) : 0,
      }))
      .sort((a, b) => b.average_score - a.average_score);

    const groupedQuestionsByText = new Map<string, any>();
    problemQuestions.forEach((question: any) => {
      const displayText = String(question.question_text || '').trim().replace(/\s+/g, ' ');
      const normalizedText = displayText.toLocaleLowerCase('ru-RU');
      if (!normalizedText) return;

      const totalAnswers = Number(question.total_answers || 0);
      const wrongAnswers = Number(question.wrong_answers || 0);
      const averageTime = Number(question.average_time_seconds || 0);
      const current = groupedQuestionsByText.get(normalizedText) || {
        question_id: question.question_id,
        question_ids: [],
        question_text: displayText,
        total_answers: 0,
        wrong_answers: 0,
        correct_answers: 0,
        weighted_time_seconds: 0,
        versions_count: 0,
      };
      current.question_ids.push(question.question_id);
      current.total_answers += totalAnswers;
      current.wrong_answers += wrongAnswers;
      current.correct_answers += Math.max(0, totalAnswers - wrongAnswers);
      current.weighted_time_seconds += averageTime * totalAnswers;
      current.versions_count += 1;
      groupedQuestionsByText.set(normalizedText, current);
    });

    const groupedProblemQuestions = Array.from(groupedQuestionsByText.values()).map((question: any) => ({
      ...question,
      question_label: `${question.question_text} — ответов: ${question.total_answers}`,
      error_rate: question.total_answers > 0
        ? Number(((question.wrong_answers / question.total_answers) * 100).toFixed(2))
        : 0,
      average_time_seconds: question.total_answers > 0
        ? Number((question.weighted_time_seconds / question.total_answers).toFixed(1))
        : 0,
    }));

    const sortedProblemQuestions = groupedProblemQuestions.sort((a: any, b: any) =>
      b.error_rate - a.error_rate || b.wrong_answers - a.wrong_answers
    );
    const problemQuestionsFailed100All = sortedProblemQuestions.filter((question: any) => {
      const totalAnswers = Number(question.total_answers || 0);
      const wrongAnswers = Number(question.wrong_answers || 0);
      return totalAnswers > 0 && wrongAnswers === totalAnswers;
    });
    const problemQuestionsFailed100 = problemQuestionsFailed100All.slice(0, questionStatsTopSize);

    return {
      topUsers,
      problemQuestions: groupedProblemQuestions,
      completedAttempts,
      countedAttempts,
      passedAttempts,
      failedAttempts,
      daily,
      testsPerformance,
      usersByScore: [...topUsers].sort((a: any, b: any) => b.average_score - a.average_score),
      usersByAttempts: [...topUsers].sort((a: any, b: any) => b.attempts_count - a.attempts_count),
      usersWithoutAttempts: topUsers.filter((user: any) => Number(user.attempts_count || 0) === 0),
      problemQuestionsTop: sortedProblemQuestions,
      problemQuestionsFailed100,
      problemQuestionsFailed100Total: problemQuestionsFailed100All.length,
      latestAttempts: completedAttempts.slice(0, 8),
    };
  }, [attempts, detailedStats, questionStatsTopSize]);

  const renderHorizontalBars = (
    rows: any[],
    valueKey: string,
    labelKey: string,
    options: { suffix?: string; color?: string; max?: number; limit?: number; fullLabel?: boolean } = {}
  ) => {
    const visibleRows = rows.slice(0, options.limit || rows.length);
    const maxValue = options.max ?? Math.max(1, ...visibleRows.map((row) => Number(row[valueKey] || 0)));

    if (!visibleRows.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />;
    }

    return (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {visibleRows.map((row, index) => {
          const value = Number(row[valueKey] || 0);
          const width = Math.max(4, Math.min(100, (value / maxValue) * 100));
          const labelStyle = options.fullLabel
            ? { maxWidth: '70%', whiteSpace: 'normal' as const, wordBreak: 'break-word' as const }
            : { maxWidth: '70%' };
          return (
            <div key={`${row[labelKey] || index}-${index}`}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                <Typography.Text ellipsis={!options.fullLabel} style={labelStyle}>
                  {row[labelKey] || 'Без названия'}
                </Typography.Text>
                <Typography.Text strong>
                  {valueKey === 'average_score' ? Math.round(value) : value}{options.suffix || ''}
                </Typography.Text>
              </div>
              <div style={{ height: 10, background: '#f0f2f5', borderRadius: 999, overflow: 'hidden' }}>
                <div
                  style={{
                    height: '100%',
                    width: `${width}%`,
                    background: options.color || '#1677ff',
                    borderRadius: 999,
                  }}
                />
              </div>
            </div>
          );
        })}
      </Space>
    );
  };

  const renderStackedUserBars = (rows: any[]) => {
    const visibleRows = rows;
    if (!visibleRows.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />;
    }

    return (
      <Space direction="vertical" size={10} style={{ width: '100%' }}>
        {visibleRows.map((row: any) => {
          const attemptsCount = Number(row.attempts_count || 0);
          const passedWidth = attemptsCount ? (Number(row.passed_count || 0) / attemptsCount) * 100 : 0;
          const failedWidth = attemptsCount ? Math.max(0, 100 - passedWidth) : 0;
          return (
            <div key={row.user_id}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 4 }}>
                <Typography.Text style={{ maxWidth: '70%', whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.user_name}</Typography.Text>
                <Typography.Text type="secondary">{attemptsCount} попыток</Typography.Text>
              </div>
              <div style={{ display: 'flex', height: 12, borderRadius: 999, overflow: 'hidden', background: '#f0f2f5' }}>
                <div style={{ width: `${passedWidth}%`, background: '#52c41a' }} />
                <div style={{ width: `${failedWidth}%`, background: '#ff4d4f' }} />
              </div>
            </div>
          );
        })}
      </Space>
    );
  };

  const renderDonut = () => {
    const total = dashboardStats.passedAttempts + dashboardStats.failedAttempts;
    const passedPercent = total ? Math.round((dashboardStats.passedAttempts / total) * 100) : 0;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
        <div
          style={{
            width: 160,
            height: 160,
            borderRadius: '50%',
            background: `conic-gradient(#52c41a 0 ${passedPercent}%, #ff4d4f ${passedPercent}% 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              width: 104,
              height: 104,
              borderRadius: '50%',
              background: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Typography.Text strong style={{ fontSize: 28 }}>{passedPercent}%</Typography.Text>
            <Typography.Text type="secondary">сдано</Typography.Text>
          </div>
        </div>
        <Space direction="vertical">
          <Tag color="success">Сдано: {dashboardStats.passedAttempts}</Tag>
          <Tag color="error">Не сдано: {dashboardStats.failedAttempts}</Tag>
          <Typography.Text type="secondary">Всего завершено: {total}</Typography.Text>
        </Space>
      </div>
    );
  };

  const renderLineChart = () => {
    const points = dashboardStats.daily;
    if (!points.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />;
    }

    const width = 640;
    const height = 220;
    const padX = 36;
    const padY = 28;
    const plotWidth = width - padX * 2;
    const plotHeight = height - padY * 2;
    const pointString = points.map((point, index) => {
      const x = padX + (points.length === 1 ? plotWidth / 2 : (plotWidth / (points.length - 1)) * index);
      const y = padY + plotHeight - (Math.max(0, Math.min(100, point.average_score)) / 100) * plotHeight;
      return `${x},${y}`;
    }).join(' ');

    return (
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', minHeight: 220 }}>
        <line x1={padX} y1={padY} x2={padX} y2={height - padY} stroke="#d9d9d9" />
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke="#d9d9d9" />
        {[0, 50, 100].map((value) => {
          const y = padY + plotHeight - (value / 100) * plotHeight;
          return (
            <g key={value}>
              <line x1={padX} y1={y} x2={width - padX} y2={y} stroke="#f0f0f0" />
              <text x={8} y={y + 4} fontSize="11" fill="#8c8c8c">{value}%</text>
            </g>
          );
        })}
        <polyline points={pointString} fill="none" stroke="#1677ff" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" />
        {points.map((point, index) => {
          const x = padX + (points.length === 1 ? plotWidth / 2 : (plotWidth / (points.length - 1)) * index);
          const y = padY + plotHeight - (Math.max(0, Math.min(100, point.average_score)) / 100) * plotHeight;
          return (
            <g key={point.date}>
              <circle cx={x} cy={y} r="4" fill="#1677ff" />
              <text x={x} y={height - 8} textAnchor="middle" fontSize="11" fill="#595959">{point.label}</text>
            </g>
          );
        })}
      </svg>
    );
  };

  const renderQuestionHeatmap = () => {
    const rows = dashboardStats.problemQuestionsFailed100;
    if (!rows.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет данных" />;
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
        {rows.map((question: any) => {
          const rate = Number(question.error_rate || 0);
          const alpha = Math.max(0.08, Math.min(0.35, rate / 220));
          return (
            <div
              key={question.question_ids.join('-')}
              style={{
                border: '1px solid #f0f0f0',
                borderRadius: 8,
                padding: 12,
                background: `rgba(255, 77, 79, ${alpha})`,
              }}
            >
              <Typography.Text strong>{rate}% ошибок</Typography.Text>
              <Typography.Paragraph style={{ marginTop: 8, marginBottom: 0 }}>
                {question.question_text}
              </Typography.Paragraph>
              <Typography.Text type="secondary">
                Ответов: {question.total_answers} · правильных: {question.correct_answers} · ошибок: {question.wrong_answers}
              </Typography.Text>
            </div>
          );
        })}
      </div>
    );
  };

  const renderQuestionAnswerSummary = (answer: any) => {
    if (!answer) return <Text type="secondary">—</Text>;
    const selectedTexts = Array.isArray(answer.selected_answer_texts) ? answer.selected_answer_texts : [];
    if (selectedTexts.length > 0) {
      return <span>{selectedTexts.join(', ')}</span>;
    }
    if (answer.answer_text) {
      return <span>{answer.answer_text}</span>;
    }
    if (answer.matching_data && typeof answer.matching_data === 'object' && Object.keys(answer.matching_data).length > 0) {
      return (
        <Space direction="vertical" size={2}>
          {Object.entries(answer.matching_data).map(([key, value]) => (
            <span key={key}>{key} → {String(value)}</span>
          ))}
        </Space>
      );
    }
    return <Text type="secondary">Ответ не выбран</Text>;
  };

  return (
    <Layout wide>
      <div className="admin-panel-page">
        <div className="admin-panel-header">
          <div>
            <h1 className="page-title">{t('admin.title')}</h1>
            <p className="page-subtitle">{t('admin.subtitle')}</p>
          </div>
        </div>

        <Tabs 
          activeKey={activeTab}
          size="large"
          centered
          className="admin-tabs"
          onChange={(key) => {
            setActiveTab(key);
            const targetRoute = adminTabRoutes[key];
            if (targetRoute && location.pathname !== targetRoute) {
              navigate(targetRoute);
            }
            if (key === 'statistics') {
              fetchDetailedStats();
              fetchAttempts();
            }
            if (key === 'dashboards') {
              fetchAttempts();
              if (!detailedStats) {
                fetchDetailedStats();
              }
            }
          }}
        >
        <TabPane
          tab={<span className="admin-tab-label"><MorphingIcon icon={activeTab === 'news' ? CircleCheck : Bell} /><span><strong>Новости</strong><small>Изменения</small></span></span>}
          key="news"
          className="admin-panel-tabpane"
        >
          <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 16 }}>
            <Alert
              type="info"
              showIcon
              message="Что изменилось в админке"
              description="Здесь собраны последние изменения простым языком и короткие инструкции по новым функциям."
            />

            <Card size="small">
              <Row gutter={[16, 12]} align="middle">
                <Col xs={24} md={16}>
                  <Select
                    mode="multiple"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    maxTagCount="responsive"
                    placeholder="Поиск по тегам"
                    value={selectedAdminNewsTags}
                    options={adminNewsTagOptions}
                    onChange={setSelectedAdminNewsTags}
                    style={{ width: '100%' }}
                  />
                </Col>
                <Col xs={24} md={8}>
                  <Typography.Text type="secondary">
                    Показано: {filteredAdminNewsItems.length} из {sortedAdminNewsItems.length}
                  </Typography.Text>
                </Col>
              </Row>
            </Card>

            {filteredAdminNewsItems.length === 0 && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="Новостей со всеми выбранными тегами не найдено"
              />
            )}

            {filteredAdminNewsItems.map((item) => (
              <Card
                key={item.title}
                title={
                  <Space size={8} wrap>
                    {getAdminNewsTags(item).map((tag) => (
                      <Tag
                        key={`${item.title}-${tag.label}`}
                        color={tag.color}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selectedAdminNewsTags.includes(tag.label)}
                        onClick={() =>
                          setSelectedAdminNewsTags((current) =>
                            current.includes(tag.label)
                              ? current.filter((label) => label !== tag.label)
                              : [...current, tag.label]
                          )
                        }
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            setSelectedAdminNewsTags((current) =>
                              current.includes(tag.label)
                                ? current.filter((label) => label !== tag.label)
                                : [...current, tag.label]
                            );
                          }
                        }}
                        style={{
                          cursor: 'pointer',
                          boxShadow: selectedAdminNewsTags.includes(tag.label)
                            ? '0 0 0 2px rgba(22, 119, 255, 0.28)'
                            : undefined,
                        }}
                      >
                        {tag.label}
                      </Tag>
                    ))}
                    <Typography.Text strong>{item.title}</Typography.Text>
                  </Space>
                }
                extra={<Typography.Text type="secondary">{item.date}</Typography.Text>}
              >
                <Row gutter={[24, 16]}>
                  <Col xs={24} lg={12}>
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      Что изменилось
                    </Typography.Title>
                    <ul style={{ margin: 0, paddingLeft: 20 }}>
                      {item.changes.map((text) => (
                        <li key={text} style={{ marginBottom: 8 }}>
                          {text}
                        </li>
                      ))}
                    </ul>
                  </Col>
                  <Col xs={24} lg={12}>
                    <Typography.Title level={5} style={{ marginTop: 0 }}>
                      Как пользоваться
                    </Typography.Title>
                    <ol style={{ margin: 0, paddingLeft: 20 }}>
                      {item.instructions.map((text) => (
                        <li key={text} style={{ marginBottom: 8 }}>
                          {text}
                        </li>
                      ))}
                    </ol>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        </TabPane>

        <TabPane
          tab={<span className="admin-tab-label"><MorphingIcon icon={activeTab === 'dashboards' ? CircleCheck : Gauge} /><span><strong>Дашборды</strong><small>Обзор данных</small></span></span>}
          key="dashboards"
          className="admin-panel-tabpane"
        >
          <Space direction="vertical" size="large" style={{ width: '100%', marginTop: 16 }}>
            <Alert
              type="info"
              showIcon
              message="Дашборды по статистике"
              description="Здесь собраны визуальные отчеты по общей статистике, пользователям, тестам и вопросам. Региональный вариант не включен."
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
              <Typography.Text type="secondary">
                В показателях учитывается последняя завершённая попытка сотрудника по каждому тесту.
              </Typography.Text>
              <Button
                onClick={() => {
                  fetchDetailedStats();
                  fetchAttempts();
                }}
                loading={loadingStats || loadingAttempts}
              >
                Обновить дашборды
              </Button>
            </div>

            {(loadingStats || loadingAttempts) && !detailedStats ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : (
              <Tabs defaultActiveKey="overview">
                <TabPane tab="Обзор" key="overview">
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12} lg={4}>
                        <div className="stat-card stat-card-primary fade-in">
                          <Statistic title="Пользователей" value={stats?.total_users || dashboardStats.topUsers.length || 0} />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={4}>
                        <div className="stat-card stat-card-success fade-in">
                          <Statistic title="Учтено результатов" value={stats?.total_attempts ?? dashboardStats.countedAttempts.length} />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={4}>
                        <div className="stat-card stat-card-warning fade-in">
                          <Statistic title="Сдано" value={stats?.passed_attempts ?? dashboardStats.passedAttempts} />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={4}>
                        <div className="stat-card stat-card-danger fade-in">
                          <Statistic title="Средний балл" value={Math.round(stats?.average_score || 0)} suffix="%" />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={4}>
                        <div className="stat-card fade-in">
                          <Statistic title="Всего вопросов" value={stats?.total_questions || 0} />
                        </div>
                      </Col>
                      <Col xs={24} sm={12} lg={4}>
                        <div className="stat-card fade-in">
                          <Statistic title="Уникальных вопросов" value={stats?.unique_questions || 0} />
                        </div>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={10}>
                        <Card title="Сдано / не сдано">
                          {renderDonut()}
                        </Card>
                      </Col>
                      <Col xs={24} lg={14}>
                        <Card title="Средний балл по датам">
                          {renderLineChart()}
                        </Card>
                      </Col>
                    </Row>

                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="Попытки по датам">
                          {renderHorizontalBars(dashboardStats.daily, 'attempts', 'label', {
                            color: '#13c2c2',
                            limit: 14,
                          })}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title="Последние завершенные попытки">
                          <Table
                            dataSource={dashboardStats.latestAttempts}
                            rowKey="id"
                            pagination={false}
                            size="small"
                            columns={[
                              { title: 'Пользователь', dataIndex: 'user_name', key: 'user_name' },
                              { title: 'Тест', dataIndex: 'test_title', key: 'test_title' },
                              {
                                title: 'Баллы',
                                dataIndex: 'score',
                                key: 'score',
                                render: (score: number) => (typeof score === 'number' ? `${score.toFixed(1)}%` : '—'),
                              },
                            ]}
                          />
                        </Card>
                      </Col>
                    </Row>
                  </Space>
                </TabPane>

                <TabPane tab="Пользователи" key="users">
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="Рейтинг по среднему баллу (все пользователи)">
                          {renderHorizontalBars(dashboardStats.usersByScore, 'average_score', 'user_name', {
                            suffix: '%',
                            color: '#52c41a',
                            max: 100,
                            fullLabel: true,
                          })}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title="Сдано / не сдано по всем пользователям">
                          {renderStackedUserBars(dashboardStats.topUsers)}
                        </Card>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="Попытки по всем пользователям">
                          {renderHorizontalBars(dashboardStats.usersByAttempts, 'attempts_count', 'user_name', {
                            color: '#1677ff',
                            fullLabel: true,
                          })}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title={`Пользователи без попыток (${dashboardStats.usersWithoutAttempts.length})`}>
                          {dashboardStats.usersWithoutAttempts.length ? (
                            <Table
                              dataSource={dashboardStats.usersWithoutAttempts}
                              rowKey="user_id"
                              pagination={false}
                              size="small"
                              columns={[
                                { title: 'Пользователь', dataIndex: 'user_name', key: 'user_name' },
                                { title: 'Отдел', dataIndex: 'department', key: 'department' },
                                { title: 'Должность', dataIndex: 'position', key: 'position' },
                                { title: 'Стаж', dataIndex: 'work_experience', key: 'work_experience' },
                                { title: 'Регион', dataIndex: 'region', key: 'region' },
                              ]}
                            />
                          ) : (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Нет пользователей без попыток" />
                          )}
                        </Card>
                      </Col>
                    </Row>
                  </Space>
                </TabPane>

                <TabPane tab="Тесты и вопросы" key="tests">
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <Card size="small" title="Фильтры статистики вопросов">
                      <Row gutter={[12, 12]} align="middle">
                        <Col xs={24} md={9}>
                          <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Все тесты"
                            value={questionStatsTestId}
                            onChange={(value) => setQuestionStatsTestId(value)}
                            options={tests.map((test: any) => ({
                              value: test.id,
                              label: `${test.title} · #${test.id}`,
                            }))}
                            style={{ width: '100%' }}
                          />
                        </Col>
                        <Col xs={24} md={7}>
                          <RangePicker
                            value={questionStatsDateRange}
                            onChange={(value) => setQuestionStatsDateRange(value)}
                            format="DD.MM.YYYY"
                            placeholder={['Дата от', 'Дата до']}
                            style={{ width: '100%' }}
                          />
                        </Col>
                        <Col xs={12} md={4}>
                          <Select
                            value={questionStatsTopSize}
                            onChange={(value) => setQuestionStatsTopSize(value)}
                            options={[10, 15, 20].map((value) => ({ value, label: `Топ ${value}` }))}
                            style={{ width: '100%' }}
                          />
                        </Col>
                        <Col xs={12} md={4}>
                          <Button
                            block
                            disabled={!questionStatsTestId && !questionStatsDateRange && questionStatsTopSize === 10}
                            onClick={() => {
                              setQuestionStatsTestId(undefined);
                              setQuestionStatsDateRange(null);
                              setQuestionStatsTopSize(10);
                            }}
                          >
                            Сбросить
                          </Button>
                        </Col>
                      </Row>
                      <Typography.Text type="secondary" style={{ display: 'block', marginTop: 10 }}>
                        {questionStatsTestId
                          ? `Тест: ${tests.find((test: any) => test.id === questionStatsTestId)?.title || `#${questionStatsTestId}`}`
                          : 'Все тесты'}
                        {' · '}
                        {questionStatsDateRange
                          ? `${questionStatsDateRange[0].format('DD.MM.YYYY')}–${questionStatsDateRange[1].format('DD.MM.YYYY')}`
                          : 'за всё время'}
                      </Typography.Text>
                    </Card>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title="Средний балл по тестам">
                          {renderHorizontalBars(dashboardStats.testsPerformance, 'average_score', 'test_title', {
                            suffix: '%',
                            color: '#722ed1',
                            max: 100,
                            limit: 12,
                          })}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title="Процент сдачи по тестам">
                          {renderHorizontalBars(dashboardStats.testsPerformance, 'pass_rate', 'test_title', {
                            suffix: '%',
                            color: '#52c41a',
                            max: 100,
                            limit: 12,
                          })}
                        </Card>
                      </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                      <Col xs={24} lg={12}>
                        <Card title={`Топ-${questionStatsTopSize} формулировок со 100% ошибками (${dashboardStats.problemQuestionsFailed100.length} из ${dashboardStats.problemQuestionsFailed100Total})`}>
                          {renderHorizontalBars(dashboardStats.problemQuestionsFailed100, 'error_rate', 'question_label', {
                            suffix: '%',
                            color: '#ff4d4f',
                            max: 100,
                            fullLabel: true,
                          })}
                        </Card>
                      </Col>
                      <Col xs={24} lg={12}>
                        <Card title={`Среднее время: топ-${questionStatsTopSize} формулировок со 100% ошибками`}>
                          {renderHorizontalBars(dashboardStats.problemQuestionsFailed100, 'average_time_seconds', 'question_label', {
                            suffix: 'с',
                            color: '#faad14',
                            fullLabel: true,
                          })}
                        </Card>
                      </Col>
                    </Row>
                    <Card title={`Тепловая карта: топ-${questionStatsTopSize} формулировок со 100% ошибками (${dashboardStats.problemQuestionsFailed100.length} из ${dashboardStats.problemQuestionsFailed100Total})`}>
                      {renderQuestionHeatmap()}
                    </Card>
                  </Space>
                </TabPane>
              </Tabs>
            )}
          </Space>
        </TabPane>

        <TabPane
          tab={<span className="admin-tab-label"><MorphingIcon icon={activeTab === 'users' ? CircleCheck : UsersIcon} /><span><strong>{t('admin.users')}</strong><small>Сотрудники</small></span></span>}
          key="users"
          className="admin-panel-tabpane"
        >
          <section className="admin-users-section" aria-labelledby="admin-users-title">
            <div className="admin-users-toolbar">
              <div>
                <Typography.Title level={3} id="admin-users-title">{t('admin.users.title')}</Typography.Title>
                <Text type="secondary">Структура компании по отделам и рабочие данные сотрудников.</Text>
              </div>
              <Space size="middle" wrap>
                <Button
                  type="default"
                  icon={<MorphingIcon icon={isUserStructureExpanded ? ChevronUp : ChevronDown} size={16} />}
                  size="large"
                  onClick={() => setIsUserStructureExpanded((current) => !current)}
                  aria-expanded={isUserStructureExpanded}
                >
                  {isUserStructureExpanded ? 'Свернуть структуру' : 'Показать структуру'}
                </Button>
                <Upload {...userProfileImportUploadProps}>
                  <Button type="default" icon={<UploadOutlined />} size="large">
                    Импорт данных
                  </Button>
                </Upload>
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  size="large"
                  href={AUTH_ADMIN_USERS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Учётные записи и пароли
                </Button>
              </Space>
            </div>

            <Alert
              type="info"
              showIcon
              className="admin-users-auth-alert"
              message="Вход выполняется через единую авторизацию PikinLab"
              description="Создание учётных записей, роли и сброс пароля выполняются в центральной админке. Здесь можно менять рабочие данные, переводить сотрудников между отделами и отключать доступ к FEBEST с сохранением истории тестов."
              action={(
                <Button
                  type="primary"
                  icon={<LockOutlined />}
                  href={AUTH_ADMIN_USERS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Открыть управление паролями
                </Button>
              )}
            />

            {users.length ? (
              <div className="admin-users-hierarchy">
                <div className="admin-users-root-wrap">
                  <button
                    type="button"
                    className="admin-users-root-node"
                    onClick={() => setIsUserStructureExpanded((current) => !current)}
                    aria-expanded={isUserStructureExpanded}
                  >
                    <MorphingIcon icon={UsersRound} size={22} />
                    <span>
                      <strong>FEBEST</strong>
                      <small>{users.length} сотрудников · {userDepartments.length} отделов · удобный каталог</small>
                    </span>
                    <MorphingIcon
                      className="admin-users-root-toggle-icon"
                      icon={isUserStructureExpanded ? ChevronUp : ChevronDown}
                      size={18}
                    />
                  </button>
                </div>

                {isUserStructureExpanded && (
                  <div className="admin-users-directory">
                    <aside className="admin-users-directory-nav" aria-label="Навигация по сотрудникам">
                      <Input
                        allowClear
                        aria-label="Найти сотрудника"
                        prefix={<SearchOutlined />}
                        placeholder="Имя, должность, отдел…"
                        value={userDirectorySearch}
                        onChange={(event) => {
                          const nextValue = event.target.value;
                          setUserDirectorySearch(nextValue);
                          if (nextValue.trim()) setSelectedUserDepartment(ALL_USER_DEPARTMENTS);
                        }}
                      />

                      <Select
                        aria-label="Статус сотрудников"
                        value={userActivityFilter}
                        onChange={(value) => {
                          setUserActivityFilter(value);
                          setSelectedUserDepartment(ALL_USER_DEPARTMENTS);
                        }}
                        style={{ width: '100%' }}
                        options={[
                          { value: 'active', label: 'Активные' },
                          { value: 'inactive', label: 'Неактивные' },
                          { value: 'all', label: 'Все статусы' },
                        ]}
                      />

                      <nav className="admin-users-department-nav" aria-label="Отделы компании">
                        <button
                          type="button"
                          className={selectedUserDepartment === ALL_USER_DEPARTMENTS ? 'is-active' : ''}
                          onClick={() => setSelectedUserDepartment(ALL_USER_DEPARTMENTS)}
                          aria-current={selectedUserDepartment === ALL_USER_DEPARTMENTS ? 'page' : undefined}
                        >
                          <MorphingIcon icon={UsersRound} size={17} />
                          <span><strong>Все сотрудники</strong><small>Вся компания</small></span>
                          <b>{filteredUserDepartments.reduce((total, department) => total + department.users.length, 0)}</b>
                        </button>

                        {userDepartments.map((department) => {
                          const matchingDepartment = filteredUserDepartments.find((item) => item.name === department.name);
                          const matchingCount = matchingDepartment?.users.length || 0;
                          const isActive = selectedUserDepartment === department.name;
                          return (
                            <button
                              type="button"
                              className={isActive ? 'is-active' : ''}
                              key={department.name}
                              onClick={() => setSelectedUserDepartment(department.name)}
                              aria-current={isActive ? 'page' : undefined}
                            >
                              <MorphingIcon icon={Building2} size={17} />
                              <span>
                                <strong>{department.name}</strong>
                                <small>{department.users.length} {department.users.length === 1 ? 'сотрудник' : 'сотрудников'}</small>
                              </span>
                              <b className={userDirectorySearch.trim() && matchingCount === 0 ? 'is-empty' : ''}>
                                {userDirectorySearch.trim() ? matchingCount : department.users.length}
                              </b>
                            </button>
                          );
                        })}
                      </nav>
                    </aside>

                    <section className="admin-users-directory-content" aria-live="polite">
                      <header className="admin-users-directory-heading">
                        <div>
                          <Typography.Title level={4}>{visibleDirectoryTitle}</Typography.Title>
                          <Text type="secondary">
                            {visibleDirectoryUsers.length} из {directoryUsers.length} сотрудников
                          </Text>
                        </div>
                        {userDirectorySearch.trim() && (
                          <Button onClick={() => setUserDirectorySearch('')}>Сбросить поиск</Button>
                        )}
                      </header>

                      {visibleDirectoryUsers.length ? (
                        <div className="admin-user-cards-grid">
                          {visibleDirectoryUsers.map((user) => {
                          const initials = String(user.full_name || user.email || '?')
                            .split(/\s+/)
                            .filter(Boolean)
                            .slice(0, 2)
                            .map((part) => part[0]?.toUpperCase())
                            .join('');
                          const questionsExpanded = userQuestionsModalUser?.id === user.id;
                          return (
                            <div className="admin-user-node" key={user.id}>
                              <article className="admin-user-tile">
                                <div className="admin-user-tile-head">
                                  <span className="admin-user-avatar" aria-hidden="true">{initials}</span>
                                  <span className="admin-user-identity">
                                    <strong>{user.full_name || 'Имя не указано'}</strong>
                                    <a href={`mailto:${user.email}`}>{user.email}</a>
                                  </span>
                                  <Tag color={user.role === 'admin' ? 'red' : 'blue'}>
                                    {user.role === 'admin' ? 'Администратор' : 'Сотрудник'}
                                  </Tag>
                                </div>

                                {!user.is_active && <Tag>Неактивен</Tag>}

                                <div className="admin-user-tile-details">
                                  <span><MorphingIcon icon={BriefcaseBusiness} size={15} /><small>Должность</small><b>{user.position || 'Не указана'}</b></span>
                                  <span><MorphingIcon icon={Clock3} size={15} /><small>Стаж</small><b>{user.work_experience || 'Не указан'}</b></span>
                                  <span><MorphingIcon icon={MapPin} size={15} /><small>Регион</small><b>{user.region || 'Не указан'}</b></span>
                                  <span><MorphingIcon icon={MonitorDot} size={15} /><small>IP-адрес</small><b>{user.last_login_ip || '—'}</b></span>
                                </div>

                                <div className="admin-user-tile-actions">
                                  <Button icon={<EditOutlined />} onClick={() => openUserProfileModal(user)}>
                                    Рабочие данные
                                  </Button>
                                  <Button
                                    icon={<SwapOutlined />}
                                    onClick={() => {
                                      setTransferUser(user);
                                      setTransferDepartment(undefined);
                                    }}
                                  >
                                    Перевести в отдел
                                  </Button>
                                  <Button
                                    icon={<LockOutlined />}
                                    href={getAuthAdminUserUrl(user.email)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    title="Открыть центральное управление учётной записью, доступом и паролем"
                                  >
                                    Пароль и доступ
                                  </Button>
                                  <Button
                                    danger={user.is_active}
                                    icon={user.is_active ? <StopOutlined /> : <CheckCircleOutlined />}
                                    disabled={user.id === currentUser?.id}
                                    loading={updatingUserActivity === user.id}
                                    onClick={() => handleUserActivityChange(user)}
                                    title={user.id === currentUser?.id ? 'Нельзя деактивировать самого себя' : undefined}
                                  >
                                    {user.is_active ? 'Деактивировать' : 'Активировать'}
                                  </Button>
                                </div>

                                {user.role === 'employee' && (
                                  <div className="admin-user-test-actions">
                                    <Button
                                      className="admin-user-attempts-button"
                                      icon={<MorphingIcon icon={ClipboardCheck} size={16} />}
                                      onClick={() => openUserAttemptsModal(user)}
                                    >
                                      Прохождения тестов
                                    </Button>
                                    <Button
                                      className="admin-user-questions-toggle"
                                      type={questionsExpanded ? 'primary' : 'default'}
                                      icon={<MorphingIcon icon={questionsExpanded ? ChevronUp : ChevronDown} size={16} />}
                                      loading={Boolean(loadingUnansweredQuestionsByUser[user.id])}
                                      onClick={() => openUserQuestionsPanel(user)}
                                      aria-expanded={questionsExpanded}
                                    >
                                      Непройденные вопросы
                                    </Button>
                                  </div>
                                )}
                              </article>
                            </div>
                          );
                          })}
                        </div>
                      ) : (
                        <Empty
                          image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="По выбранным условиям сотрудники не найдены"
                        >
                          <Button onClick={() => {
                            setUserDirectorySearch('');
                            setSelectedUserDepartment(ALL_USER_DEPARTMENTS);
                          }}>
                            Показать всех
                          </Button>
                        </Empty>
                      )}
                    </section>
                  </div>
                )}
              </div>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Сотрудники не найдены" />
            )}
          </section>
        </TabPane>

        {/* Вкладка «Тесты» удалена: каталог, назначения, объединение,
            история и импорт изображений переехали в «Назначения»
            (/admin/board). */}

        <TabPane
          tab={<span className="admin-tab-label"><MorphingIcon icon={activeTab === 'statistics' ? CircleCheck : ChartNoAxesColumn} /><span><strong>{t('admin.statistics')}</strong><small>Результаты</small></span></span>}
          key="statistics"
          className="admin-panel-tabpane"
        >
          <Tabs activeKey={statisticsView} onChange={setStatisticsView}>
          <TabPane tab="Общая статистика" key="details">
          <Card 
            title={<span style={{ fontSize: '18px', fontWeight: 600 }}>{t('admin.statistics.details')}</span>}
            headStyle={{ 
              paddingLeft: 24,
              paddingRight: 24
            }}
            style={{ marginTop: 16 }}
            extra={
              <Button onClick={fetchDetailedStats} loading={loadingStats}>
                Обновить
              </Button>
            }
          >
            {loadingStats ? (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Spin size="large" />
              </div>
            ) : detailedStats ? (
              <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic title="Всего тестов" value={stats?.total_tests || tests.length || 0} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic title="Активных тестов" value={stats?.active_tests || 0} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic title="Всего вопросов" value={stats?.total_questions || 0} />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} lg={6}>
                    <Card size="small">
                      <Statistic title="Уникальных вопросов" value={stats?.unique_questions || 0} />
                    </Card>
                  </Col>
                </Row>

                {/* Статистика по регионам */}
                {detailedStats.regions && detailedStats.regions.length > 0 && (
                  <div>
                    <Typography.Title level={4}>Статистика по регионам</Typography.Title>
                    <Table
                      dataSource={detailedStats.regions}
                      columns={[
                        { title: 'Регион', dataIndex: 'region', key: 'region' },
                        { title: 'Пользователей', dataIndex: 'users_count', key: 'users_count' },
                        { title: 'Попыток', dataIndex: 'attempts_count', key: 'attempts_count' },
                        { title: 'Сдано', dataIndex: 'passed_count', key: 'passed_count' },
                        { title: 'Не сдано', dataIndex: 'failed_count', key: 'failed_count' },
                        { 
                          title: 'Средний балл', 
                          dataIndex: 'average_score', 
                          key: 'average_score',
                          render: (score: number) => `${Math.round(score)}%`
                        }
                      ]}
                      rowKey="region"
                      pagination={false}
                      size="small"
                    />
                  </div>
                )}

                {/* Рейтинг по отделам */}
                {detailedStats.departments && detailedStats.departments.length > 0 && (
                  <div>
                    <Typography.Title level={4}>Рейтинг прохождения тестов по отделу</Typography.Title>
                    <Table
                      dataSource={detailedStats.departments}
                      columns={[
                        { 
                          title: 'Отдел', 
                          dataIndex: 'department', 
                          key: 'department',
                          render: (dept: string, record: any, index: number) => (
                            <Space>
                              {index < 3 && (
                                <span style={{ fontSize: '18px' }}>
                                  {index === 0 ? '🥇' : index === 1 ? '🥈' : '🥉'}
                                </span>
                              )}
                              <span>{dept}</span>
                            </Space>
                          )
                        },
                        { title: 'Пользователей', dataIndex: 'users_count', key: 'users_count' },
                        { title: 'Попыток', dataIndex: 'attempts_count', key: 'attempts_count' },
                        { title: 'Сдано', dataIndex: 'passed_count', key: 'passed_count' },
                        { title: 'Не сдано', dataIndex: 'failed_count', key: 'failed_count' },
                        { 
                          title: 'Средний балл', 
                          dataIndex: 'average_score', 
                          key: 'average_score',
                          render: (score: number) => (
                            <Tag color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error'}>
                              {Math.round(score)}%
                            </Tag>
                          ),
                          sorter: (a: any, b: any) => b.average_score - a.average_score
                        }
                      ]}
                      rowKey="department"
                      pagination={false}
                      size="small"
                    />
                  </div>
                )}

                {/* Статистика по стажу работы */}
                {detailedStats.work_experience && detailedStats.work_experience.length > 0 && (
                  <div>
                    <Typography.Title level={4}>Статистика по стажу работы</Typography.Title>
                    <Table
                      dataSource={detailedStats.work_experience}
                      columns={[
                        {
                          title: 'Стаж работы',
                          dataIndex: 'work_experience',
                          key: 'work_experience',
                        },
                        { title: 'Пользователей', dataIndex: 'users_count', key: 'users_count' },
                        { title: 'Попыток', dataIndex: 'attempts_count', key: 'attempts_count' },
                        { title: 'Сдано', dataIndex: 'passed_count', key: 'passed_count' },
                        { title: 'Не сдано', dataIndex: 'failed_count', key: 'failed_count' },
                        {
                          title: 'Средний балл',
                          dataIndex: 'average_score',
                          key: 'average_score',
                          render: (score: number) => (
                            <Tag color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error'}>
                              {Math.round(score)}%
                            </Tag>
                          ),
                          sorter: (a: any, b: any) => b.average_score - a.average_score
                        }
                      ]}
                      rowKey="work_experience"
                      pagination={false}
                      size="small"
                    />
                  </div>
                )}

                {/* Сотрудники, которые начали тест, но не завершили его до конца лимита времени */}
                <div>
                  <Typography.Title level={4} style={{ marginBottom: 4 }}>
                    Брошенные тесты ({detailedStats.abandoned_attempts?.length || 0})
                  </Typography.Title>
                  <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
                    Не сданные попытки, в которых сотрудник завершил тест, ответив не на все вопросы.
                    Активные и технически незавершённые записи не учитываются.
                  </Typography.Paragraph>
                  {detailedStats.abandoned_attempts?.length ? (
                    <Table
                      dataSource={detailedStats.abandoned_attempts}
                      rowKey="attempt_id"
                      size="small"
                      pagination={detailedStats.abandoned_attempts.length > 10 ? {
                        pageSize: 10,
                        showSizeChanger: true,
                        size: 'small',
                        showTotal: (total: number) => `всего ${total}`,
                      } : false}
                      scroll={{ x: 1050 }}
                      columns={[
                        {
                          title: 'Сотрудник',
                          dataIndex: 'user_name',
                          key: 'user_name',
                          width: 210,
                          render: (name: string, record: any) => (
                            <div>
                              <Typography.Text strong>{name}</Typography.Text>
                              <Typography.Text type="secondary" style={{ display: 'block', fontSize: 12 }}>
                                {record.position}
                              </Typography.Text>
                            </div>
                          ),
                        },
                        { title: 'Отдел', dataIndex: 'department', key: 'department', width: 190 },
                        { title: 'Тест', dataIndex: 'test_title', key: 'test_title', width: 260 },
                        {
                          title: 'Дата',
                          dataIndex: 'completed_at',
                          key: 'completed_at',
                          width: 150,
                          render: (value: string, record: any) =>
                            dayjs.utc(value || record.started_at).tz(getTimezoneByRegion(record.region)).format('DD.MM.YYYY HH:mm'),
                        },
                        {
                          title: 'Ответил',
                          key: 'progress',
                          width: 110,
                          render: (_: unknown, record: any) => (
                            <Tag color="warning">
                              {record.answered_questions} / {record.estimated_total_questions}
                            </Tag>
                          ),
                        },
                        {
                          title: 'Верных',
                          dataIndex: 'correct_answers',
                          key: 'correct_answers',
                          width: 90,
                          render: (value: number) => <Tag color="success">{value}</Tag>,
                        },
                        {
                          title: 'Оценка',
                          dataIndex: 'score',
                          key: 'score',
                          width: 100,
                          render: (value: number) => <Tag color="error">{value}%</Tag>,
                        },
                      ]}
                    />
                  ) : (
                    <Alert type="success" showIcon message="Брошенных тестов нет" />
                  )}
                </div>

                {/* Проблемные вопросы */}
                {detailedStats.problem_questions && detailedStats.problem_questions.length > 0 && (
                  <div>
                    <Button
                      type="text"
                      onClick={() => setProblemQuestionsOpen((value) => !value)}
                      style={{ padding: 0, height: 'auto', marginBottom: 12 }}
                      icon={problemQuestionsOpen ? <UpOutlined /> : <DownOutlined />}
                    >
                      <Typography.Title level={4} style={{ margin: 0, display: 'inline' }}>
                        Самые проблемные вопросы ({detailedStats.problem_questions.length})
                      </Typography.Title>
                    </Button>
                    {problemQuestionsOpen && (
                    <Table
                      dataSource={detailedStats.problem_questions}
                      columns={[
                        { 
                          title: 'Вопрос', 
                          dataIndex: 'question_text', 
                          key: 'question_text',
                          width: 400,
                          render: (text: string) => (
                            <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                              {text}
                            </div>
                          )
                        },
                        { title: 'Всего ответов', dataIndex: 'total_answers', key: 'total_answers' },
                        { 
                          title: 'Правильных', 
                          dataIndex: 'correct_answers', 
                          key: 'correct_answers',
                          render: (val: number) => <Tag color="success">{val}</Tag>
                        },
                        { 
                          title: 'Неправильных', 
                          dataIndex: 'wrong_answers', 
                          key: 'wrong_answers',
                          render: (val: number) => <Tag color="error">{val}</Tag>
                        },
                        { 
                          title: 'Процент ошибок', 
                          dataIndex: 'error_rate', 
                          key: 'error_rate',
                          render: (rate: number) => (
                            <Tag color={rate > 50 ? 'error' : rate > 30 ? 'warning' : 'default'}>
                              {rate}%
                            </Tag>
                          )
                        },
                        { 
                          title: 'Среднее время', 
                          dataIndex: 'average_time_seconds', 
                          key: 'average_time_seconds',
                                    render: (seconds: number) => formatTime(seconds)
                        }
                      ]}
                      rowKey="question_id"
                      pagination={{
                        pageSize: 10,
                        showSizeChanger: true,
                        size: 'small',
                        showTotal: (total: number) => `всего ${total}`,
                      }}
                      size="small"
                      expandable={{
                        expandedRowKeys: Array.from(expandedQuestionRows),
                        onExpand: (expanded, record: any) => {
                          if (expanded) {
                            fetchQuestionDetails(record.question_id);
                          } else {
                            setExpandedQuestionRows(prev => {
                              const newSet = new Set(prev);
                              newSet.delete(record.question_id);
                              return newSet;
                            });
                          }
                        },
                        expandedRowRender: (record: any) => {
                          if (loadingQuestionDetails[record.question_id]) {
                            return (
                              <div style={{ padding: '20px', textAlign: 'center' }}>
                                <Spin />
                              </div>
                            );
                          }

                          const questionDetail = questionDetails[record.question_id];
                          if (!questionDetail) {
                            return (
                              <div style={{ padding: '20px', color: '#999' }}>
                                Нет данных о вопросе
                              </div>
                            );
                          }

                          const timeStats = questionDetail.time_statistics || {};

                          return (
                            <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                              <div style={{ marginBottom: 16 }}>
                                <Typography.Text strong>Правильный ответ: </Typography.Text>
                                <Tag color="success" style={{ marginLeft: 8 }}>
                                  {questionDetail.correct_answers.answer_texts.join(', ')}
                                </Tag>
                              </div>
                              {questionDetail.question_image_url && (
                                <div style={{ marginBottom: 16, textAlign: 'center' }}>
                                  <Image
                                    src={getStaticImageUrl(questionDetail.question_image_url)}
                                    alt="Изображение вопроса"
                                    style={{ maxWidth: '300px', maxHeight: '200px' }}
                                    preview
                                  />
                                </div>
                              )}
                              
                              <Typography.Title level={5} style={{ marginBottom: 16 }}>
                                Статистика по неправильным ответам
                              </Typography.Title>
                              
                              <Row gutter={16} style={{ marginBottom: 16 }}>
                                <Col span={6}>
                                  <Card size="small">
                                    <Statistic
                                      title="Количество неправильных ответов"
                                      value={questionDetail.wrong_answers_count || 0}
                                      valueStyle={{ color: '#ff4d4f' }}
                                    />
                                  </Card>
                                </Col>
                                <Col span={6}>
                                  <Card size="small">
                                    <Statistic
                                      title="Среднее время на неправильный ответ"
                                      value={formatTime(timeStats.average_time_seconds || 0)}
                                      valueStyle={{ color: '#1890ff' }}
                                    />
                                  </Card>
                                </Col>
                                <Col span={6}>
                                  <Card size="small">
                                    <Statistic
                                      title="Минимальное время"
                                      value={formatTime(timeStats.min_time_seconds || 0)}
                                      valueStyle={{ color: '#52c41a' }}
                                    />
                                  </Card>
                                </Col>
                                <Col span={6}>
                                  <Card size="small">
                                    <Statistic
                                      title="Максимальное время"
                                      value={formatTime(timeStats.max_time_seconds || 0)}
                                      valueStyle={{ color: '#faad14' }}
                                    />
                                  </Card>
                                </Col>
                              </Row>
                              
                              <Card size="small">
                                <Statistic
                                  title="Общее время, затраченное на неправильные ответы"
                                  value={formatTime(timeStats.total_time_seconds || 0)}
                                  valueStyle={{ color: '#722ed1', fontSize: '24px' }}
                                />
                              </Card>

                              <Card
                                size="small"
                                title={`Сотрудники, ответившие на вопрос (${questionDetail.answered_users_count || 0})`}
                                style={{ marginTop: 16 }}
                              >
                                <Table
                                  dataSource={questionDetail.respondents || []}
                                  rowKey="user_id"
                                  pagination={false}
                                  size="small"
                                  columns={[
                                    {
                                      title: 'Сотрудник',
                                      dataIndex: 'user_name',
                                      key: 'user_name',
                                      width: 240,
                                      render: (name: string, respondent: any) => (
                                        <Space direction="vertical" size={0}>
                                          <Text strong>{name}</Text>
                                          <Text type="secondary">{respondent.user_department || 'Отдел не указан'}</Text>
                                          <Text type="secondary">{respondent.user_work_experience || 'Стаж не указан'}</Text>
                                          <Text type="secondary">{respondent.user_region}</Text>
                                        </Space>
                                      ),
                                    },
                                    { title: 'Ответов', dataIndex: 'answers_count', key: 'answers_count', width: 90 },
                                    {
                                      title: 'Правильно',
                                      dataIndex: 'correct_answers',
                                      key: 'correct_answers',
                                      width: 110,
                                      render: (value: number) => <Tag color="success">{value || 0}</Tag>,
                                    },
                                    {
                                      title: 'Ошибок',
                                      dataIndex: 'wrong_answers',
                                      key: 'wrong_answers',
                                      width: 100,
                                      render: (value: number) => <Tag color="error">{value || 0}</Tag>,
                                    },
                                    {
                                      title: 'Среднее время',
                                      dataIndex: 'average_time_seconds',
                                      key: 'average_time_seconds',
                                      width: 140,
                                      render: (seconds: number) => formatTime(seconds || 0),
                                    },
                                    {
                                      title: 'Последний ответ',
                                      dataIndex: 'latest_answer',
                                      key: 'latest_answer',
                                      render: (answer: any) => (
                                        <div style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                          {renderQuestionAnswerSummary(answer)}
                                        </div>
                                      ),
                                    },
                                    {
                                      title: 'Статус',
                                      dataIndex: 'latest_is_correct',
                                      key: 'latest_is_correct',
                                      width: 120,
                                      render: (value: boolean | null) => (
                                        value === true
                                          ? <Tag color="success">Верно</Tag>
                                          : value === false
                                            ? <Tag color="error">Ошибка</Tag>
                                            : <Tag>Нет данных</Tag>
                                      ),
                                    },
                                  ]}
                                />
                              </Card>
                            </div>
                          );
                        }
                      }}
                    />
                    )}
                  </div>
                )}

                {/* Полный рейтинг пользователей */}
                {(
                  <div className="user-statistics-ranking">
                    <Typography.Title level={4}>
                      Все пользователи по среднему баллу ({userRanking.users.length})
                    </Typography.Title>
                    <Space wrap style={{ marginBottom: 12 }}>
                      <label>
                        <Text style={{ marginRight: 8 }}>Месяц</Text>
                        <DatePicker picker="month" placeholder="Выберите месяц" format="MM.YYYY"
                          aria-label="Месяц рейтинга" value={rankingMonth}
                          onChange={value => changeUserStatsPeriod(value ? {
                            from: value.startOf('month').format('YYYY-MM-DD'),
                            to: value.endOf('month').format('YYYY-MM-DD'),
                          } : null)} />
                      </label>
                      <label>
                        <Text style={{ marginRight: 8 }}>Период</Text>
                        <RangePicker value={rankingRange} format="DD.MM.YYYY"
                          placeholder={['С даты', 'По дату']}
                          onChange={values => changeUserStatsPeriod(values?.[0] && values?.[1] ? {
                            from: values[0].format('YYYY-MM-DD'), to: values[1].format('YYYY-MM-DD'),
                          } : null)} />
                      </label>
                      <Button disabled={!userRanking.period} onClick={() => changeUserStatsPeriod(null)}>За всё время</Button>
                    </Space>
                    <Typography.Paragraph type="secondary">
                      {rankingRange ? `${rankingRange[0].format('DD.MM.YYYY')} — ${rankingRange[1].format('DD.MM.YYYY')}` : 'За всё время'}.
                      {' '}По дате завершения, московское время. По каждому тесту учитывается последняя попытка в выбранном периоде.
                    </Typography.Paragraph>
                    {userRanking.error && <Alert type="error" showIcon message={userRanking.error}
                      action={<Button onClick={() => changeUserStatsPeriod(userRanking.period)}>Повторить</Button>} style={{ marginBottom: 12 }} />}
                    <Table
                      loading={userRanking.loading}
                      dataSource={userRanking.users}
                      columns={[
                        { title: 'Пользователь', dataIndex: 'user_name', key: 'user_name' },
                        { title: 'Отдел', dataIndex: 'department', key: 'department' },
                        { title: 'Должность', dataIndex: 'position', key: 'position' },
                        { title: 'Стаж', dataIndex: 'work_experience', key: 'work_experience' },
                        { title: 'Регион', dataIndex: 'region', key: 'region' },
                        {
                          title: 'Попыток',
                          dataIndex: 'attempts_count',
                          key: 'attempts_count',
                          sorter: (a: any, b: any) => a.attempts_count - b.attempts_count,
                        },
                        { 
                          title: 'Сдано', 
                          dataIndex: 'passed_count', 
                          key: 'passed_count',
                          sorter: (a: any, b: any) => a.passed_count - b.passed_count,
                          render: (val: number) => <Tag color="success">{val}</Tag>
                        },
                        { 
                          title: 'Не сдано', 
                          dataIndex: 'failed_count', 
                          key: 'failed_count',
                          sorter: (a: any, b: any) => a.failed_count - b.failed_count,
                          render: (val: number) => <Tag color="error">{val}</Tag>
                        },
                        { 
                          title: 'Средний балл', 
                          dataIndex: 'average_score', 
                          key: 'average_score',
                          defaultSortOrder: 'descend' as const,
                          sorter: (a: any, b: any) => a.average_score - b.average_score,
                          render: (score: number) => (
                            <Tag color={score >= 70 ? 'success' : score >= 50 ? 'warning' : 'error'}>
                              {Math.round(score)}%
                            </Tag>
                          )
                        }
                      ]}
                      rowKey="user_id"
                      scroll={{ x: 1200 }}
                      pagination={{
                        current: userStatsPagination.current,
                        pageSize: userStatsPagination.pageSize,
                        showSizeChanger: true,
                        pageSizeOptions: ['20', '50', '100', '200'],
                        showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} пользователей`,
                        locale: {
                          items_per_page: ' / стр.',
                        },
                        onChange: (page, pageSize) => {
                          setUserStatsPagination((prev) => ({
                            current: page,
                            pageSize: pageSize || prev.pageSize,
                          }));
                        },
                        onShowSizeChange: (_current, size) => {
                          setUserStatsPagination({ current: 1, pageSize: size });
                        },
                      }}
                      size="small"
                      expandable={{
                        expandedRowKeys: Array.from(expandedUserRows),
                        onExpand: (expanded, record: UserStatistic) => {
                          if (expanded) {
                            userRanking.loadDetails(record.user_id);
                          } else {
                            userRanking.closeDetails(record.user_id);
                          }
                        },
                        expandedRowRender: (record: UserStatistic) => {
                          if (loadingUserDetails[record.user_id]) {
                            return (
                              <div style={{ padding: '20px', textAlign: 'center' }}>
                                <Spin />
                              </div>
                            );
                          }

                          if (userRanking.detailErrors[record.user_id]) {
                            return <Alert type="error" message={userRanking.detailErrors[record.user_id]}
                              action={<Button onClick={() => userRanking.loadDetails(record.user_id)}>Повторить</Button>} />;
                          }
                          const details = userDetails[record.user_id];
                          if (!details || !details.tests || details.tests.length === 0) {
                            return (
                              <div style={{ padding: '20px', color: '#999' }}>
                                Нет завершённых тестов за выбранный период
                              </div>
                            );
                          }

                          return (
                            <div style={{ padding: '16px' }}>
                              <Typography.Title level={5} style={{ marginBottom: 16 }}>
                                Завершённые попытки ({details.total_tests})
                              </Typography.Title>
                              <Table
                                dataSource={details.tests}
                                columns={[
                                  {
                                    title: 'Тест',
                                    dataIndex: 'test_title',
                                    key: 'test_title',
                                    render: (text: string, record: any) => (
                                      <div>
                                        <Typography.Link
                                          href={`/admin/tests/${record.test_id}/questions`}
                                          strong
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            if (
                                              event.button === 0
                                              && !event.metaKey
                                              && !event.ctrlKey
                                              && !event.shiftKey
                                              && !event.altKey
                                            ) {
                                              event.preventDefault();
                                              navigate(`/admin/tests/${record.test_id}/questions`);
                                            }
                                          }}
                                        >
                                          {text}
                                        </Typography.Link>
                                        {record.test_block && (
                                          <Tag style={{ marginLeft: 8 }}>{record.test_block}</Tag>
                                        )}
                                        {record.has_assignment === false && <Tag style={{ marginLeft: 8 }}>Без назначения</Tag>}
                                        {record.is_counted === false && <Tag style={{ marginLeft: 8 }}>Заменена пересдачей</Tag>}
                                      </div>
                                    )
                                  },
                                  {
                                    title: 'Дата прохождения',
                                    dataIndex: 'completed_at',
                                    key: 'completed_at',
                                    render: (date: string) => {
                                      if (!date) return '-';
                                      const d = dayjs.utc(date);
                                      const timezone = getTimezoneByRegion(details.region || 'Москва');
                                      return d.tz(timezone).format('DD.MM.YYYY HH:mm');
                                    }
                                  },
                                  {
                                    title: 'Результат',
                                    dataIndex: 'score',
                                    key: 'score',
                                    render: (score: number, record: any) => (
                                      <Tag color={record.is_passed ? 'success' : 'error'}>
                                        {score}%
                                      </Tag>
                                    )
                                  },
                                  {
                                    title: 'Правильных',
                                    dataIndex: 'correct_answers',
                                    key: 'correct_answers',
                                    render: (val: number, record: any) => (
                                      <span>
                                        <Tag color="success">{val}</Tag> / {record.total_questions}
                                      </span>
                                    )
                                  },
                                  {
                                    title: 'Неправильных',
                                    dataIndex: 'wrong_answers',
                                    key: 'wrong_answers',
                                    render: (val: number) => (
                                      <Tag color="error">{val}</Tag>
                                    )
                                  },
                                  {
                                    title: 'Время',
                                    dataIndex: 'time_spent_seconds',
                                    key: 'time_spent_seconds',
                                    render: (seconds: number) => formatTime(seconds)
                                  },
                                  {
                                    title: 'Среднее время на вопрос',
                                    dataIndex: 'average_time_per_question',
                                    key: 'average_time_per_question',
                                    render: (seconds: number) => {
                                      if (!seconds) return '-';
                                      return `${seconds}с`;
                                    }
                                  }
                                ]}
                                rowKey="attempt_id"
                                pagination={false}
                                size="small"
                                expandable={{
                                  expandedRowKeys: Array.from(expandedTestRows),
                                  onExpand: (expanded, record: any) => {
                                    if (expanded) {
                                      fetchTestDetails(record.attempt_id);
                                    } else {
                                      setExpandedTestRows(prev => {
                                        const newSet = new Set(prev);
                                        newSet.delete(record.attempt_id);
                                        return newSet;
                                      });
                                    }
                                  },
                                  expandedRowRender: (record: any) => {
                                    if (loadingTestDetails[record.attempt_id]) {
                                      return (
                                        <div style={{ padding: '20px', textAlign: 'center' }}>
                                          <Spin />
                                        </div>
                                      );
                                    }

                                    const testDetail = testDetails[record.attempt_id];
                                    if (!testDetail || !testDetail.detailed_answers || testDetail.detailed_answers.length === 0) {
                                      return (
                                        <div style={{ padding: '20px', color: '#999' }}>
                                          Нет детальной информации о вопросах
                                        </div>
                                      );
                                    }

                                    return (
                                      <div style={{ padding: '16px', background: '#fafafa', borderRadius: '8px' }}>
                                        <Typography.Title level={5} style={{ marginBottom: 16 }}>
                                          Детальная статистика по вопросам
                                        </Typography.Title>
                                        <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                          {testDetail.detailed_answers.map((qa: any, index: number) => (
                                            <Card
                                              key={qa.question_id}
                                              size="small"
                                              style={{
                                                borderColor: qa.user_answer.is_correct ? '#52c41a' : '#ff4d4f',
                                                borderWidth: 2
                                              }}
                                            >
                                              <div style={{ marginBottom: 12 }}>
                                                <Space>
                                                  <Typography.Text strong>
                                                    Вопрос {index + 1}:
                                                  </Typography.Text>
                                                  <Tag color={qa.user_answer.is_correct ? 'success' : 'error'}>
                                                    {qa.user_answer.is_correct ? '✓ Правильно' : '✗ Неправильно'}
                                                  </Tag>
                                                </Space>
                                              </div>
                                              <Typography.Paragraph style={{ marginBottom: 12, fontSize: '15px' }}>
                                                {qa.question_text}
                                              </Typography.Paragraph>
                                              
                                              {qa.question_image_url && (
                                                <div style={{ marginBottom: 12, textAlign: 'center' }}>
                                                  <Image
                                                    src={getStaticImageUrl(qa.question_image_url)}
                                                    alt="Изображение вопроса"
                                                    style={{ maxWidth: '300px', maxHeight: '200px' }}
                                                    preview
                                                  />
                                                </div>
                                              )}

                                              {(qa.question_type === 'single_choice' || qa.question_type === 'multiple_choice') && Array.isArray(qa.all_answers) && qa.all_answers.length > 0 && (
                                                <div style={{ marginBottom: 12 }}>
                                                  <Typography.Text strong>
                                                    Все варианты ответа:
                                                  </Typography.Text>
                                                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    {(() => {
                                                      const selectedAnswerIds = new Set(
                                                        (Array.isArray(qa.user_answer?.selected_answer_ids)
                                                          ? qa.user_answer.selected_answer_ids
                                                          : qa.user_answer?.selected_answer_ids
                                                            ? [qa.user_answer.selected_answer_ids]
                                                            : [])
                                                          .map((value: any) => Number(value))
                                                          .filter((value: number) => Number.isFinite(value))
                                                      );

                                                      return qa.all_answers.map((answer: any, answerIndex: number) => {
                                                        const isSelected = selectedAnswerIds.has(Number(answer.id));

                                                        return (
                                                          <div
                                                            key={answer.id}
                                                            style={{
                                                              display: 'flex',
                                                              alignItems: 'center',
                                                              gap: 10,
                                                              padding: '8px 12px',
                                                              border: `1px solid ${answer.is_correct ? '#b7eb8f' : isSelected ? '#91caff' : '#d9d9d9'}`,
                                                              borderRadius: 4,
                                                              background: answer.is_correct ? '#f6ffed' : isSelected ? '#e6f4ff' : '#fff'
                                                            }}
                                                          >
                                                            <Typography.Text strong style={{ minWidth: 22 }}>
                                                              {answerIndex + 1}.
                                                            </Typography.Text>
                                                            {answer.image_url && (
                                                              <Image
                                                                src={getStaticImageUrl(answer.image_url)}
                                                                alt={answer.text}
                                                                width={72}
                                                                style={{ maxHeight: 72, objectFit: 'contain' }}
                                                                preview
                                                              />
                                                            )}
                                                            <Typography.Text style={{ flex: 1 }}>
                                                              {answer.text}
                                                            </Typography.Text>
                                                            <Space size={4} wrap>
                                                              {isSelected && <Tag color="processing">Выбран пользователем</Tag>}
                                                              {answer.is_correct && <Tag color="success">Правильный</Tag>}
                                                            </Space>
                                                          </div>
                                                        );
                                                      });
                                                    })()}
                                                  </div>
                                                </div>
                                              )}

                                              <div style={{ marginBottom: 8 }}>
                                                <Typography.Text strong style={{ color: '#1890ff' }}>
                                                  Ответ пользователя:
                                                </Typography.Text>
                                                <div style={{ marginTop: 8, padding: '8px 12px', background: '#e6f7ff', borderRadius: '4px', borderLeft: '3px solid #1890ff' }}>
                                                  {(() => {
                                                    // Отладочная информация (можно убрать после исправления)
                                                    if (process.env.NODE_ENV === 'development') {
                                                      console.log('Question answer data:', {
                                                        question_type: qa.question_type,
                                                        user_answer: qa.user_answer,
                                                        selected_answer_ids: qa.user_answer?.selected_answer_ids,
                                                        all_answers: qa.all_answers
                                                      });
                                                    }
                                                    
                                                    if (qa.question_type === 'single_choice' || qa.question_type === 'multiple_choice') {
                                                      const selectedIds = qa.user_answer?.selected_answer_ids;
                                                      // Нормализуем selected_answer_ids - убеждаемся, что это массив
                                                      const normalizedIds = (Array.isArray(selectedIds) ? selectedIds : (selectedIds ? [selectedIds] : []))
                                                        .map((value: any) => Number(value))
                                                        .filter((value: number) => Number.isFinite(value));
                                                      
                                                      if (normalizedIds.length > 0) {
                                                        const foundAnswers = normalizedIds
                                                          .map((answerId: number) => {
                                                            const answer = qa.all_answers.find((a: any) => Number(a.id) === answerId);
                                                            return answer ? { id: answerId, ...answer } : null;
                                                          })
                                                          .filter((a: any) => a !== null);
                                                        
                                                        if (foundAnswers.length > 0) {
                                                          return (
                                                            <div>
                                                              {foundAnswers.map((answer: any) => (
                                                                <div key={answer.id} style={{ marginBottom: 4 }}>
                                                                  {answer.image_url && (
                                                                    <img 
                                                                      src={getStaticImageUrl(answer.image_url)}
                                                                      alt={answer.text}
                                                                      style={{ maxWidth: '100px', maxHeight: '100px', marginRight: 8, verticalAlign: 'middle' }}
                                                                    />
                                                                  )}
                                                                  {answer.text}
                                                                </div>
                                                              ))}
                                                            </div>
                                                          );
                                                        }
                                                      }
                                                      return <div style={{ color: '#999', fontStyle: 'italic' }}>Ответ не выбран</div>;
                                                    } else if (qa.question_type === 'open_answer' || qa.question_type === 'image_identification') {
                                                      return <div>{qa.user_answer?.answer_text || 'Нет ответа'}</div>;
                                                    } else if (qa.question_type === 'matching') {
                                                      const matchingData = qa.user_answer?.matching_data;
                                                      if (matchingData && typeof matchingData === 'object' && Object.keys(matchingData).length > 0) {
                                                        return (
                                                          <div>
                                                            {Object.entries(matchingData).map(([key, value]) => (
                                                              <div key={key}>{key} → {value as string}</div>
                                                            ))}
                                                          </div>
                                                        );
                                                      }
                                                      return <div style={{ color: '#999', fontStyle: 'italic' }}>Ответ не выбран</div>;
                                                    }
                                                    return <div style={{ color: '#999', fontStyle: 'italic' }}>Неизвестный тип вопроса</div>;
                                                  })()}
                                                </div>
                                              </div>

                                              <div>
                                                <Typography.Text strong style={{ color: '#52c41a' }}>
                                                  Правильный ответ:
                                                </Typography.Text>
                                                <div style={{ marginTop: 8, padding: '8px 12px', background: '#f6ffed', borderRadius: '4px', borderLeft: '3px solid #52c41a' }}>
                                                  {qa.question_type === 'single_choice' || qa.question_type === 'multiple_choice' ? (
                                                    <div>
                                                      {qa.correct_answers.answer_texts.map((text: string, idx: number) => {
                                                        const answer = qa.all_answers.find((a: any) => a.text === text && a.is_correct);
                                                        return (
                                                          <div key={idx} style={{ marginBottom: 4 }}>
                                                            {answer?.image_url && (
                                                              <img 
                                                                src={getStaticImageUrl(answer.image_url)}
                                                                alt={text}
                                                                style={{ maxWidth: '100px', maxHeight: '100px', marginRight: 8, verticalAlign: 'middle' }}
                                                              />
                                                            )}
                                                            {text}
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  ) : qa.question_type === 'open_answer' || qa.question_type === 'image_identification' ? (
                                                    <div>{qa.correct_answers.answer_texts[0] || 'Нет правильного ответа'}</div>
                                                  ) : qa.question_type === 'matching' ? (
                                                    <div>
                                                      {Object.entries(qa.correct_answers.matching || {}).map(([key, value]) => (
                                                        <div key={key}>{key} → {value as string}</div>
                                                      ))}
                                                    </div>
                                                  ) : null}
                                                </div>
                                              </div>

                                              {qa.user_answer.time_spent_seconds && (
                                                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                                                  Время на вопрос: {qa.user_answer.time_spent_seconds}с
                                                </div>
                                              )}
                                            </Card>
                                          ))}
                                        </Space>
                                      </div>
                                    );
                                  }
                                }}
                              />
                            </div>
                          );
                        }
                      }}
                    />
                  </div>
                )}

                {(!detailedStats.regions || detailedStats.regions.length === 0) &&
                 (!detailedStats.departments || detailedStats.departments.length === 0) &&
                 (!detailedStats.work_experience || detailedStats.work_experience.length === 0) &&
                 (!detailedStats.problem_questions || detailedStats.problem_questions.length === 0) &&
                 (!userRanking.users.length && !userRanking.loading && !userRanking.error) && (
                  <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                    Нет данных для отображения
                  </div>
                )}
              </Space>
            ) : (
              <div style={{ textAlign: 'center', padding: '50px' }}>
                <Button type="primary" onClick={fetchDetailedStats}>
                  Загрузить статистику
                </Button>
              </div>
            )}

            <Divider />
            {initialTab === 'statistics' && <EmployeeRetakeStatistics />}

            {/* Управление попытками */}
            <Divider />
            <div className="admin-attempts-section" style={{ marginTop: 32 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Typography.Title level={4} style={{ margin: 0 }}>Все попытки прохождения тестов</Typography.Title>
                <Space wrap style={{ maxWidth: '100%' }}>
                  <AttemptsExcelExportButton userSearch={attemptUserSearch} department={attemptDepartment} testId={attemptTestId}
                    dateFrom={attemptDateFrom} dateTo={attemptDateTo} />
                  <Button onClick={fetchAttempts} loading={loadingAttempts}>
                    Обновить
                  </Button>
                  <Button
                    danger
                    disabled={selectedAttemptIds.length === 0}
                    onClick={handleDeleteSelectedAttempts}
                  >
                    Удалить выбранные{selectedAttemptIds.length > 0 ? ` (${selectedAttemptIds.length})` : ''}
                  </Button>
                  <Button danger onClick={handleDeleteAllAttempts}>
                    Удалить все попытки
                  </Button>
                </Space>
              </div>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  aria-label="Поиск пользователя в попытках"
                  placeholder="Поиск по имени или фамилии"
                  value={attemptUserSearch}
                  onChange={(event) => {
                    setAttemptUserSearch(event.target.value);
                    resetAttemptSelection();
                  }}
                  style={{ width: 360, maxWidth: '100%' }}
                />
                <Select
                  allowClear
                  showSearch
                  aria-label="Фильтр по отделу"
                  placeholder="Все отделы"
                  optionFilterProp="label"
                  options={attemptDepartmentOptions}
                  value={attemptDepartment}
                  onChange={(value) => {
                    setAttemptDepartment(value);
                    resetAttemptSelection();
                  }}
                  style={{ width: 280, maxWidth: '100%' }}
                />
                <Select
                  allowClear
                  showSearch
                  aria-label="Фильтр по тесту"
                  placeholder="Все тесты"
                  optionFilterProp="label"
                  options={attemptTestOptions}
                  value={attemptTestId}
                  onChange={(value) => {
                    setAttemptTestId(value);
                    resetAttemptSelection();
                  }}
                  style={{ width: 360, maxWidth: '100%' }}
                />
                <div style={{ width: 360, maxWidth: '100%' }}>
                  <RangePicker
                    aria-label="Период прохождения тестов"
                    placeholder={['С даты', 'По дату']}
                    format="DD.MM.YYYY"
                    allowEmpty={[true, true]}
                    value={attemptDateRange}
                    onChange={(dates) => {
                      setAttemptDateRange(dates);
                      resetAttemptSelection();
                    }}
                    style={{ width: '100%' }}
                  />
                  <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>По дате завершения, время московское</Text>
                </div>
                {hasAttemptFilters && (
                  <>
                    <Text type="secondary">Найдено попыток: {filteredAttempts.length} из {attempts.length}</Text>
                    <Button onClick={() => {
                      setAttemptUserSearch('');
                      setAttemptDepartment(undefined);
                      setAttemptTestId(undefined);
                      setAttemptDateRange(null);
                      resetAttemptSelection();
                    }}>Сбросить фильтры</Button>
                  </>
                )}
              </div>
              
              {loadingAttempts ? (
                <div style={{ textAlign: 'center', padding: '50px' }}>
                  <Spin size="large" />
                </div>
              ) : attempts.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                  Нет попыток для отображения
                </div>
              ) : (
                <Table
                  dataSource={filteredAttempts}
                  locale={{ emptyText: 'По выбранным фильтрам попытки не найдены' }}
                  rowSelection={{
                    selectedRowKeys: selectedAttemptIds,
                    onChange: (keys) => setSelectedAttemptIds(keys.map(key => Number(key))),
                    preserveSelectedRowKeys: false,
                  }}
                  columns={[
                    { 
                      title: 'Пользователь', 
                      dataIndex: 'user_name', 
                      key: 'user_name' 
                    },
                    { 
                      title: 'Регион', 
                      dataIndex: 'user_region', 
                      key: 'user_region',
                      render: (region: string) => region || 'Не указан'
                    },
                    {
                      title: 'Отдел',
                      dataIndex: 'user_department',
                      key: 'user_department',
                      render: (department: string) => department || 'Не указан'
                    },
                    {
                      title: 'Должность',
                      dataIndex: 'user_position',
                      key: 'user_position',
                      render: (position: string) => position || 'Не указан'
                    },
                    {
                      title: 'Стаж',
                      dataIndex: 'user_work_experience',
                      key: 'user_work_experience',
                      render: (workExperience: string) => workExperience || 'Не указан'
                    },
                    { 
                      title: 'Тест', 
                      dataIndex: 'test_title', 
                      key: 'test_title' 
                    },
                    { 
                      title: 'Дата начала', 
                      dataIndex: 'started_at', 
                      key: 'started_at',
                      render: (date: string, record: any) => {
                        if (!date) return '-';
                        const timezone = getTimezoneByRegion(record.user_region);
                        return dayjs.utc(date).tz(timezone).format('DD.MM.YYYY HH:mm:ss');
                      }
                    },
                    { 
                      title: 'Дата завершения', 
                      dataIndex: 'completed_at', 
                      key: 'completed_at',
                      render: (date: string, record: any) => {
                        if (!date) return '-';
                        const timezone = getTimezoneByRegion(record.user_region);
                        return dayjs.utc(date).tz(timezone).format('DD.MM.YYYY HH:mm:ss');
                      }
                    },
                    { 
                      title: 'Результат', 
                      dataIndex: 'score', 
                      key: 'score',
                      render: (score: number | null | undefined) => (
                        typeof score === 'number' ? `${score.toFixed(1)}%` : '-'
                      )
                    },
                    { 
                      title: 'Статус', 
                      dataIndex: 'is_passed', 
                      key: 'is_passed',
                      render: (isPassed: boolean) => (
                        <Tag color={isPassed ? 'success' : 'error'}>
                          {isPassed ? 'Сдан' : 'Не сдан'}
                        </Tag>
                      )
                    },
                    { 
                      title: 'Время', 
                      dataIndex: 'time_spent_seconds', 
                      key: 'time_spent_seconds',
                                    render: (seconds: number) => formatTime(seconds)
                    },
                    {
                      title: 'Действия',
                      key: 'actions',
                      render: (_: any, record: any) => (
                        <Button
                          type="link"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteAttempt(record)}
                          size="small"
                        >
                          Удалить
                        </Button>
                      )
                    }
                  ]}
                  rowKey="id"
                  scroll={{ x: 1500 }}
                  pagination={{
                    current: attemptsPagination.current,
                    pageSize: attemptsPagination.pageSize,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} попыток`,
                    locale: {
                      items_per_page: ' / стр.',
                    },
                    onChange: (page, pageSize) => {
                      setAttemptsPagination((prev) => ({
                        current: page,
                        pageSize: pageSize || prev.pageSize,
                      }));
                    },
                    onShowSizeChange: (_current, size) => {
                      setAttemptsPagination({ current: 1, pageSize: size });
                    },
                  }}
                  size="small"
                />
              )}
            </div>
          </Card>
          </TabPane>
          <TabPane tab="Накопительные итоги" key="cumulative">
            <CumulativeStatistics active={activeTab === 'statistics' && statisticsView === 'cumulative'} />
          </TabPane>
          </Tabs>
        </TabPane>
        <TabPane
          tab={<span className="admin-tab-label"><MorphingIcon icon={activeTab === 'mail' ? CircleCheck : Mail} /><span><strong>Рассылка</strong><small>Письма сотрудникам</small></span></span>}
          key="mail"
          className="admin-panel-tabpane"
        >
          <AdminMailJournal active={activeTab === 'mail'} />
        </TabPane>
      </Tabs>
      </div>

      <Modal
        title={userAttemptsModalUser ? `Прохождения тестов — ${userAttemptsModalUser.full_name}` : 'Прохождения тестов'}
        open={Boolean(userAttemptsModalUser)}
        onCancel={() => {
          setUserAttemptsModalUser(null);
          setUserAttemptsModalItems([]);
        }}
        footer={null}
        width={1050}
        destroyOnClose
      >
        <Alert
          className="admin-user-attempts-alert"
          type="info"
          showIcon
          message="Удаление прохождения очищает результат и ответы сотрудника"
          description="Если удаляется последнее завершённое прохождение назначенного теста, тест автоматически снова становится доступен сотруднику."
        />
        <Table
          className="admin-user-attempts-table"
          dataSource={userAttemptsModalItems.filter((attempt) => Boolean(attempt.is_completed))}
          loading={loadingUserAttempts}
          rowKey="id"
          size="small"
          scroll={{ x: 820 }}
          locale={{ emptyText: 'У сотрудника нет завершённых прохождений' }}
          pagination={{
            pageSize: 10,
            hideOnSinglePage: true,
            showTotal: (total) => `${total} завершённых прохождений`,
          }}
          columns={[
            {
              title: 'Тест',
              dataIndex: 'test_title',
              key: 'test_title',
              width: 340,
            },
            {
              title: 'Дата завершения',
              dataIndex: 'completed_at',
              key: 'completed_at',
              width: 170,
              render: (date: string, record: any) => date
                ? dayjs.utc(date).tz(getTimezoneByRegion(record.user_region || userAttemptsModalUser?.region)).format('DD.MM.YYYY HH:mm')
                : '—',
            },
            {
              title: 'Результат',
              dataIndex: 'score',
              key: 'score',
              width: 110,
              render: (score: number | null | undefined) => (
                typeof score === 'number' ? `${score.toFixed(1)}%` : '—'
              ),
            },
            {
              title: 'Статус',
              dataIndex: 'is_passed',
              key: 'is_passed',
              width: 110,
              render: (isPassed: boolean) => (
                <Tag color={isPassed ? 'success' : 'error'}>
                  {isPassed ? 'Сдан' : 'Не сдан'}
                </Tag>
              ),
            },
            {
              title: 'Действие',
              key: 'action',
              width: 170,
              render: (_: any, record: any) => (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleDeleteAttempt(record)}
                >
                  Удалить
                </Button>
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        title={userQuestionsModalUser ? `Непройденные вопросы — ${userQuestionsModalUser.full_name}` : 'Непройденные вопросы'}
        open={Boolean(userQuestionsModalUser)}
        onCancel={() => setUserQuestionsModalUser(null)}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {userQuestionsModalUser && renderUserUnansweredQuestions(userQuestionsModalUser)}
      </Modal>

      <Modal
        title="Импорт рабочих данных сотрудников"
        open={userProfileImportModalVisible}
        onOk={handleUserProfileImport}
        onCancel={() => {
          setUserProfileImportModalVisible(false);
          setUserProfileImportFile(null);
        }}
        okText="Импортировать"
        cancelText="Отмена"
        confirmLoading={userProfileImportSubmitting}
        width={720}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Alert
            type="info"
            showIcon
            message="Файл обновляет только существующих сотрудников"
            description="Для поиска сотрудника нужна хотя бы одна колонка: ID, Email, Логин или ФИО. Колонки Отдел, Должность, Стаж работы и Регион необязательны: если колонки или значения нет, это поле не меняется и ошибкой не считается."
          />
          {userProfileImportFile && (
            <Text>
              Файл: <Text strong>{userProfileImportFile.name}</Text>
            </Text>
          )}
          <div>
            <Typography.Title level={5} style={{ marginTop: 0 }}>
              Поддерживаемые названия колонок
            </Typography.Title>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>ID: <Text code>ID</Text>, <Text code>User ID</Text>, <Text code>ID пользователя</Text></li>
              <li>Email: <Text code>Email</Text>, <Text code>Почта</Text>, <Text code>Электронная почта</Text></li>
              <li>Логин: <Text code>Логин</Text>, <Text code>Username</Text></li>
              <li>ФИО: <Text code>ФИО</Text>, <Text code>Сотрудник</Text>, <Text code>Full name</Text></li>
              <li>Данные: <Text code>Отдел</Text>, <Text code>Должность</Text>, <Text code>Стаж работы</Text>, <Text code>Регион</Text></li>
            </ul>
          </div>
        </Space>
      </Modal>

      <Modal
        title="Добавить пользователя"
        open={userModalVisible}
        onOk={async () => {
          if (!userFormData.username || !userFormData.email || !userFormData.first_name || !userFormData.last_name || !userFormData.password) {
            message.warning('Заполните все обязательные поля');
            return;
          }

          setUserSubmitting(true);
          try {
            // Объединяем имя и фамилию в full_name для API
            const userData = {
              ...userFormData,
              full_name: `${userFormData.first_name} ${userFormData.last_name}`.trim()
            };
            await api.post('/admin/users', userData);
            message.success('Пользователь успешно создан');
            setUserModalVisible(false);
            setUserFormData({
              username: '',
              email: '',
              first_name: '',
              last_name: '',
              password: '',
              department: '',
              position: '',
              work_experience: '',
              region: '',
              role: 'employee'
            });
            fetchUsers();
          } catch (error: any) {
            message.error(getApiErrorMessage(error, 'Ошибка создания пользователя'));
          } finally {
            setUserSubmitting(false);
          }
        }}
        onCancel={() => {
          setUserModalVisible(false);
          setUserFormData({
            username: '',
            email: '',
            first_name: '',
            last_name: '',
            password: '',
            department: '',
            position: '',
            work_experience: '',
            region: '',
            role: 'employee'
          });
        }}
        okText="Создать"
        cancelText="Отмена"
        confirmLoading={userSubmitting}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Логин <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              value={userFormData.username}
              onChange={(e) => setUserFormData({ ...userFormData, username: e.target.value })}
              placeholder="Введите логин"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Email <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              type="email"
              value={userFormData.email}
              onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
              placeholder="Введите email"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Имя <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              value={userFormData.first_name}
              onChange={(e) => setUserFormData({ ...userFormData, first_name: e.target.value })}
              placeholder="Введите имя"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Фамилия <span style={{ color: 'red' }}>*</span>
            </label>
            <Input
              value={userFormData.last_name}
              onChange={(e) => setUserFormData({ ...userFormData, last_name: e.target.value })}
              placeholder="Введите фамилию"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Пароль <span style={{ color: 'red' }}>*</span>
            </label>
            <Input.Password
              value={userFormData.password}
              onChange={(e) => setUserFormData({ ...userFormData, password: e.target.value })}
              placeholder="Введите пароль"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Отдел
            </label>
            <Input
              value={userFormData.department}
              onChange={(e) => setUserFormData({ ...userFormData, department: e.target.value })}
              placeholder="Введите отдел"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Должность
            </label>
            <Input
              value={userFormData.position}
              onChange={(e) => setUserFormData({ ...userFormData, position: e.target.value })}
              placeholder="Введите должность"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Стаж работы
            </label>
            <Input
              value={userFormData.work_experience}
              onChange={(e) => setUserFormData({ ...userFormData, work_experience: e.target.value })}
              placeholder="Например: 3 года"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Регион
            </label>
            <Input
              value={userFormData.region}
              onChange={(e) => setUserFormData({ ...userFormData, region: e.target.value })}
              placeholder="Введите регион"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Роль
            </label>
            <Select
              value={userFormData.role}
              onChange={(value) => setUserFormData({ ...userFormData, role: value })}
              style={{ width: '100%' }}
              size="large"
            >
              <Option value="employee">Сотрудник</Option>
              <Option value="admin">Администратор</Option>
            </Select>
          </div>
        </Space>
      </Modal>

      <Modal
        title="Перевести сотрудника в другой отдел"
        open={Boolean(transferUser)}
        onOk={handleUserTransfer}
        onCancel={() => setTransferUser(null)}
        okText="Перевести"
        cancelText="Отмена"
        confirmLoading={transferSubmitting}
        okButtonProps={{
          disabled: transferDepartment === undefined
            || transferDepartment === (transferUser?.department?.trim() || ''),
        }}
        cancelButtonProps={{ disabled: transferSubmitting }}
        closable={!transferSubmitting}
        maskClosable={!transferSubmitting}
        keyboard={!transferSubmitting}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Text strong>{transferUser?.full_name || transferUser?.email}</Text>
          <Text>Текущий отдел: {transferUser ? getUserDepartment(transferUser) : '—'}</Text>
          <div>
            <label htmlFor="employee-transfer-department" style={{ display: 'block', marginBottom: 8 }}>
              Новый отдел
            </label>
            <Select
              id="employee-transfer-department"
              showSearch
              optionFilterProp="label"
              placeholder="Выберите отдел"
              style={{ width: '100%' }}
              options={transferDepartmentOptions}
              value={transferDepartment}
              onChange={setTransferDepartment}
              disabled={transferSubmitting}
            />
          </div>
          <Text type="secondary">Назначения тестов и история прохождений сохранятся.</Text>
        </Space>
      </Modal>

      <Modal
        title={`Редактировать данные сотрудника${editingUserProfile?.full_name ? `: ${editingUserProfile.full_name}` : ''}`}
        open={userProfileModalVisible}
        onOk={handleUserProfileSave}
        onCancel={closeUserProfileModal}
        okText="Сохранить"
        cancelText="Отмена"
        confirmLoading={userProfileSubmitting}
        width={560}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Отдел
            </label>
            <Input
              value={userProfileFormData.department}
              onChange={(e) => setUserProfileFormData({ ...userProfileFormData, department: e.target.value })}
              placeholder="Введите отдел"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Должность
            </label>
            <Input
              value={userProfileFormData.position}
              onChange={(e) => setUserProfileFormData({ ...userProfileFormData, position: e.target.value })}
              placeholder="Введите должность"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Стаж работы
            </label>
            <Input
              value={userProfileFormData.work_experience}
              onChange={(e) => setUserProfileFormData({ ...userProfileFormData, work_experience: e.target.value })}
              placeholder="Например: 3 года"
              size="large"
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>
              Регион
            </label>
            <Input
              value={userProfileFormData.region}
              onChange={(e) => setUserProfileFormData({ ...userProfileFormData, region: e.target.value })}
              placeholder="Введите регион"
              size="large"
            />
          </div>
        </Space>
      </Modal>

          <Modal
            title="Создать тест из выбранных вопросов"
            open={Boolean(unansweredTestModalUser)}
            onOk={handleCreateUnansweredQuestionsTest}
            onCancel={closeUnansweredTestModal}
            okText="Создать и назначить"
            cancelText="Отмена"
            confirmLoading={Boolean(
              unansweredTestModalUser
              && assigningUnansweredQuestionsByUser[unansweredTestModalUser.id]
            )}
            okButtonProps={{ disabled: !unansweredTestTitle.trim() }}
          >
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <Alert
                type="info"
                showIcon
                message={
                  `Выбрано вопросов: ${
                    unansweredTestModalUser
                      ? (selectedUnansweredQuestionIdsByUser[unansweredTestModalUser.id] || []).length
                      : 0
                  }`
                }
                description={
                  unansweredTestModalUser
                    ? `Новый тест будет назначен сотруднику ${unansweredTestModalUser.full_name}. Каждый выбранный вопрос останется отдельным.`
                    : undefined
                }
              />
              <div>
                <Typography.Text strong>Название теста</Typography.Text>
                <Input
                  autoFocus
                  value={unansweredTestTitle}
                  onChange={(event) => setUnansweredTestTitle(event.target.value)}
                  onPressEnter={() => {
                    if (unansweredTestTitle.trim()) {
                      handleCreateUnansweredQuestionsTest();
                    }
                  }}
                  placeholder="Введите название нового теста"
                  maxLength={255}
                  showCount
                  style={{ marginTop: 8 }}
                />
              </div>
            </Space>
          </Modal>

    </Layout>
  );
};

export default AdminPanel;
